import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Textarea } from "../components/ui/textarea";
import {
  Search,
  Plus,
  Phone,
  MoreVertical,
  Eye,
  Trash2,
  Filter,
  X,
  Globe,
  Hash,
  UserPlus,
  PhoneOutgoing,
  Loader2,
  Building2,
  Users,
  CheckSquare,
  Upload,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ChevronUp,
  History,
  Download,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Save
} from "lucide-react";
import CSVImport from "../components/CSVImport";

const PRODUCT_CATEGORIES = ["Woven", "Knits", "Non Woven"];
const INDIA_DATA_SOURCES = [
  "Importgenius data",
  "Order exists",
  "Sample exists",
  "Query exists",
  "E-Commerce Data",
  "General data",
  "Website",
  "Meta",
  "Referral",
];

const ECOMMERCE_SUB_SOURCES = [
  "Meesho",
  "Flipkart",
  "Amazon",
  "Myntra",
  "Others",
];

const BANGLADESH_DATA_SOURCES = [
  "Query exists",
  "Sample exists",
  "Order exists",
  "Meta",
  "Importgenius",
  "Referral",
  "BGME",
  "BGBA",
];

// Status options (computed based on activity)
const STATUS_OPTIONS = [
  { value: "new", label: "New", description: "No calls made yet" },
  { value: "contacted", label: "Contacted", description: "Call has been made" },
  { value: "contacted_lfb", label: "Contacted & LFB Created", description: "Call made + LFB created" },
  { value: "pick_pool", label: "Pick Pool", description: "Pushed to Pick Pool" }
];

// Allocation Status options
const ALLOCATION_STATUSES = [
  { value: "unallocated", label: "Unallocated" },
  { value: "allocated_no_action", label: "Allocated but no action" },
  { value: "lfb_created", label: "LFB Created" }
];

// Unallocated Duration options
const UNALLOCATED_DURATIONS = [
  { value: "15", label: "15+ days unallocated" },
  { value: "30", label: "30+ days unallocated" },
  { value: "45", label: "45+ days unallocated" }
];

// Fabric types
const FABRIC_TYPES = ["Greige", "Dyed", "Printed", "Denim"];

// Primary Fabric Materials
const PRIMARY_MATERIALS = ["Cotton", "Polyester", "Viscose"];

// Composition Materials for dropdown
const COMPOSITION_MATERIALS = ["Cotton", "Polyester", "Viscose", "Nylon", "Spandex", "Tencel", "Lyocell"];

// Percentage options 1-100
const PERCENTAGE_OPTIONS = Array.from({ length: 100 }, (_, i) => i + 1);

// GSM options from 25 to 500 in multiples of 5
const GSM_OPTIONS = Array.from({ length: 96 }, (_, i) => 25 + (i * 5));

// Denim OZ options from 2oz to 15oz in 0.25oz increments
const DENIM_OZ_OPTIONS = Array.from({ length: 53 }, (_, i) => (2 + (i * 0.25)).toFixed(2).replace(/\.?0+$/, ''));

// Denier options for Denim and Polyester yarn count
const DENIER_OPTIONS = [
  "20D", "30D", "40D", "50D", "70D", "75D", "100D", "150D", "200D", "250D", 
  "300D", "400D", "420D", "500D", "600D", "840D", "900D", "1000D", "1200D", "1680D"
];

// EPI/PPI options from 25 to 200
const EPI_PPI_OPTIONS = Array.from({ length: 176 }, (_, i) => 25 + i);

// Width options from 36 to 140 inches
const WIDTH_OPTIONS = Array.from({ length: 105 }, (_, i) => 36 + i);

// Finish options (non-wash)
const FINISH_OPTIONS = [
  "Bio", "Double Bio", "Silicon", "Double Silicon", 
  "Softener", "Anti-pilling", "Water Repellent", "Wrinkle Free", "Others"
];

// Wash options
const WASH_OPTIONS = [
  "Enzyme Wash", "Sulphur Wash", "Acid Wash", "Normal Wash", 
  "Stone Wash", "Bleach"
];

// Indian States
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh",
  "Andaman and Nicobar Islands", "Dadra and Nagar Haveli and Daman and Diu", "Lakshadweep"
];

const BANGLADESH_DIVISIONS = [
  "Barishal", "Chattogram", "Dhaka", "Khulna", "Mymensingh",
  "Rajshahi", "Rangpur", "Sylhet"
];

const BANGLADESH_CITIES = [
  "Dhaka", "Chittagong", "Khulna", "Rajshahi", "Sylhet",
  "Rangpur", "Barishal", "Comilla", "Gazipur", "Narayanganj",
  "Mymensingh", "Bogra", "Cox's Bazar", "Jessore", "Dinajpur",
  "Savar", "Tongi", "Narsingdi", "Tangail", "Brahmanbaria",
  "Other"
];

// Fabric qualities for Knits
const FABRIC_QUALITIES = [
  "4 Way Lycra 220-230 GSM",
  "Rice Knit 130-170 GSM",
  "Dot Knit 130-170 GSM",
  "PP Micro 110-130 GSM",
  "Mulberry Fabric 150-220 GSM",
  "Taiwan Lycra 250-270 GSM",
  "Tintin (Non-Spandex) 190 GSM",
  "Malai Fabric (Full Dull Yarn) 8-10% Spandex 170-190 GSM",
  "Malai Fabric (Full Dull Yarn) Non Spandex 170-190 GSM",
  "Others"
];

// Denim specifications
const DENIM_SPECIFICATIONS = [
  "65% Cotton 35% Poly 4.5oz, 64\" cw, Shirting Fabric",
  "78% Cotton 20% Poly 2% Spandex 9.5oz, 70\" cw, Bottom Weight",
  "100% Cotton 4.5oz, 64\" cw, Shirting Fabric",
  "100% Cotton 12oz, 65\" cw, Bottom Weight",
  "70% Cotton 30% Poly 6.25oz, 64\" cw, Shirting Fabric",
  "100% Cotton 13oz, 65\" cw, Bottom Weight",
  "Other Specification"
];

// Quantity options by category
const QUANTITY_OPTIONS = {
  Cotton: ["1,000 - 5,000 m", "5,000 - 20,000 m", "20,000 - 50,000 m", "50,000 m+"],
  Viscose: ["1,000 - 5,000 m", "5,000 - 20,000 m", "20,000 - 50,000 m", "50,000 m+"],
  Denim: ["1,000 - 2,500 m", "2,500 - 7,500 m", "7,500 - 25,000 m", "25,000 m+"],
  Knits: ["Less than 200 kg", "200 - 500 kg", "500 - 1,000 kg", "1,000 kg+"]
};

const Leads = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showSecondaryContactInAdd, setShowSecondaryContactInAdd] = useState(false);
  // Leads Excel export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFilter, setExportFilter] = useState({ type: "none", start: "", end: "" });
  const [exportDownloading, setExportDownloading] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showDedupDialog, setShowDedupDialog] = useState(false);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupData, setDedupData] = useState(null);
  const [dedupMerging, setDedupMerging] = useState(false);

  const handleOpenDedupDialog = async () => {
    setShowDedupDialog(true);
    setDedupLoading(true);
    setDedupData(null);
    try {
      const res = await axios.get(`${API}/leads/duplicates/scan`);
      setDedupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to scan duplicates");
      setShowDedupDialog(false);
    } finally {
      setDedupLoading(false);
    }
  };

  const handleConfirmMerge = async () => {
    if (!dedupData || !dedupData.groups?.length) return;
    setDedupMerging(true);
    try {
      const res = await axios.post(`${API}/leads/duplicates/merge`);
      toast.success(res.data.message || "Duplicates merged");
      setShowDedupDialog(false);
      setDedupData(null);
      fetchLeads(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to merge duplicates");
    } finally {
      setDedupMerging(false);
    }
  };
  // Sync ?order_timeline=... from URL → filters (multi)
  useEffect(() => {
    const ot = searchParams.get('order_timeline');
    if (ot && !filters.order_timeline?.includes(ot)) {
      setFilters((f) => ({ ...f, order_timeline: [ot] }));
      setShowFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [showFilters, setShowFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('crm_active_filters');
      if (saved) {
        const f = JSON.parse(saved);
        return !!((f.fabric_category?.length > 0) || (f.data_source?.length > 0) || (f.allocation_status?.length > 0) || (f.status?.length > 0) ||
          (f.payment_type?.length > 0) || (f.city?.length > 0) || (f.assigned_to?.length > 0) || (f.filter_fabric_types?.length > 0) || (f.filter_regions?.length > 0));
      }
    } catch {}
    return false;
  });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [agents, setAgents] = useState([]);
  const [csvAssignTo, setCsvAssignTo] = useState(""); // Agent to assign CSV leads to
  
  // Auth context for role check
  const { user } = useAuth();
  const isAdmin = ['admin','super_admin'].includes(user?.role);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // Mass allocation state
  const [showMassAllocate, setShowMassAllocate] = useState(false);
  const [allocateToAgent, setAllocateToAgent] = useState("");
  const [isAllocating, setIsAllocating] = useState(false);
  
  // Mass delete state
  const [showMassDelete, setShowMassDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // GST Verify state
  const [gstVerifying, setGstVerifying] = useState(false);
  const [gstVerified, setGstVerified] = useState(null);

  // Manual call log state
  const [showManualCallLog, setShowManualCallLog] = useState(false);
  const [manualCallLead, setManualCallLead] = useState(null);
  const [showCallChoiceDialog, setShowCallChoiceDialog] = useState(false);
  const [callChoiceLead, setCallChoiceLead] = useState(null);
  // Helper: derive a default follow-up date = call_date + 7 days
  const addDaysToDate = (yyyy_mm_dd, days) => {
    if (!yyyy_mm_dd) return '';
    const d = new Date(yyyy_mm_dd + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };
  const _initialLeadsCallDate = new Date().toISOString().split('T')[0];
  const [manualCallData, setManualCallData] = useState({
    call_date: _initialLeadsCallDate,
    call_time: new Date().toTimeString().slice(0, 5),
    status: "connected",
    duration_minutes: "",
    duration_seconds_input: "",
    notes: "",
    // Mandatory follow-up (auto-default to call_date + 7 days)
    followup_description: "",
    followup_date: addDaysToDate(_initialLeadsCallDate, 7),
    followup_time: "10:00",
    followup_priority: "medium",
    followup_date_touched: false,
  });
  const [callScreenshotFile, setCallScreenshotFile] = useState(null);
  const [isUploadingCallScreenshot, setIsUploadingCallScreenshot] = useState(false);
  
  // Smart filters (admin only)
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('crm_active_filters');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      fabric_category: [],
      data_source: [],
      allocation_status: [],
      status: [],  // Multi: New, Contacted, Contacted & LFB Created, Pick Pool
      search: "",
      payment_type: [],  // Multi: Cash, Credit, Not Set
      city: [],          // Multi
      // Smart filters
      last_allocation_date: "",
      last_call_date: "",
      primary_fabric_material: [],
      unallocated_days: "",
      assigned_to: [],   // Multi-select agents
      flagged: "",  // Filter for flagged leads (data quality issues)
      order_fabric_type: "",
      order_category: "",
      last_order_date: "",
      filter_fabric_types: [],
      filter_regions: [],
      score_min: "",
      score_max: "",
      order_timeline: [],   // Multi
      gst_turnover: []      // Multi
    };
  });
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveFilterName, setSaveFilterName] = useState("");
  const [showSaveFilter, setShowSaveFilter] = useState(false);
  const [newLead, setNewLead] = useState({
    name: "",
    phone: "",
    email: "",
    company_name: "",
    contact_person: "",
    contact_designation: "",
    contact_linkedin: "",
    contact_2_name: "",
    contact_2_designation: "",
    contact_2_phone: "",
    contact_2_email: "",
    contact_2_linkedin: "",
    gst_number: "",
    gst_registration_date: "",
    gst_turnover: "",
    website: "",
    has_website: "",
    business_type: "",
    data_source: "",  // Website, Meta, Referral for manual creation
    sub_data_source: "",  // Sub source for E-Commerce Data
    region: "India",  // India or Bangladesh
    quantity: "",
    quantity_unit: "",
    address: "",
    city: "",
    state: "",
    region: "",
    customer_type: "",
    payment_type: "",
    notes: "",
    // Fabric Details
    product_category: "",
    fabric_category: "",
    fabric_type: "",
    primary_material: "",
    fabric_subcategory: "",
    pattern: "",
    color: "",
    finish: "",
    wash: "",
    gsm: "",
    width_inches: "",
    // Fabric Construction
    epi: "",
    ppi: "",
    // Composition (up to 4 materials)
    material_1: "",
    material_1_percentage: "",
    material_2: "",
    material_2_percentage: "",
    material_3: "",
    material_3_percentage: "",
    material_4: "",
    material_4_percentage: "",
    // Yarn Count
    warp_count: "",
    weft_count: "",
    // Properties
    weft_shrinkage: "",
    stretch: "",
    // Sample
    sample_shared: false,
  });
  const [sampleFile, setSampleFile] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState(null);

  // GST Verify handler for Add Lead dialog
  const handleVerifyGST = async () => {
    const gstin = newLead.gst_number?.trim();
    if (!gstin || gstin.length !== 15) {
      toast.error("Enter a valid 15-character GSTIN");
      return;
    }
    setGstVerifying(true);
    setGstVerified(null);
    try {
      const res = await axios.post(`${API}/gst/lookup`, { gstin });
      const { mapped_fields, gst_status, is_active, raw_info } = res.data;
      if (!is_active) {
        toast.error(`GST is ${gst_status}. Cannot auto-fill details for inactive GST.`);
        setGstVerified({ status: gst_status, active: false });
        setGstVerifying(false);
        return;
      }
      setNewLead(prev => ({
        ...prev,
        company_name: mapped_fields.company_name || prev.company_name,
        business_type: mapped_fields.business_type || prev.business_type,
        gst_registration_date: mapped_fields.gst_registration_date || prev.gst_registration_date,
        city: mapped_fields.city || prev.city,
        state: mapped_fields.state || prev.state,
        address: mapped_fields.address || prev.address,
      }));
      setGstVerified({ status: gst_status, active: true, constitution: raw_info.constitution });
      toast.success(`GST Verified - ${mapped_fields.company_name}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "GST lookup failed";
      toast.error(msg);
      setGstVerified(null);
    } finally {
      setGstVerifying(false);
    }
  };

  // Fetch current user info
  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error("Failed to fetch current user:", error);
    }
  };

  // Fetch agents list (for admin to assign leads)
  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAgents(response.data.filter(u => u.is_active));
    } catch (error) {
      console.error("Failed to fetch agents:", error);
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const response = await axios.get(`${API}/categories/all-options`);
      setDropdownOptions(response.data);
    } catch (error) {
      console.error("Failed to fetch dropdown options:", error);
    }
  };

  // Load saved filters from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('crm_saved_filters');
      if (saved) setSavedFilters(JSON.parse(saved));
    } catch {}
  }, []);

  // Persist active filters to sessionStorage (survives navigation within the app)
  useEffect(() => {
    try {
      sessionStorage.setItem('crm_active_filters', JSON.stringify(filters));
    } catch {}
  }, [filters]);

  const handleSaveFilter = () => {
    if (!saveFilterName.trim()) return;
    const preset = { name: saveFilterName.trim(), filters: { ...filters } };
    const updated = [...savedFilters, preset];
    setSavedFilters(updated);
    localStorage.setItem('crm_saved_filters', JSON.stringify(updated));
    setSaveFilterName("");
    setShowSaveFilter(false);
    toast.success(`Filter "${preset.name}" saved`);
  };

  const handleLoadFilter = (preset) => {
    setFilters({ 
      ...preset.filters, 
      assigned_to: preset.filters.assigned_to || [],
      filter_fabric_types: preset.filters.filter_fabric_types || [],
      filter_regions: preset.filters.filter_regions || []
    });
    toast.success(`Loaded filter "${preset.name}"`);
  };

  const handleDeleteFilter = (idx) => {
    const updated = savedFilters.filter((_, i) => i !== idx);
    setSavedFilters(updated);
    localStorage.setItem('crm_saved_filters', JSON.stringify(updated));
    toast.success("Filter deleted");
  };

  // Collect distinct cities from loaded leads
  const [distinctCities, setDistinctCities] = useState([]);

  const fetchLeads = async (page = pagination.page) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      
      // Pagination params
      params.append("page", page.toString());
      params.append("limit", "50");
      
      if (filters.fabric_category?.length) params.append("fabric_category", filters.fabric_category.join(","));
      if (filters.data_source?.length) params.append("data_source", filters.data_source.join(","));
      if (filters.gst_turnover?.length) params.append("gst_turnover", filters.gst_turnover.join(","));
      if (filters.allocation_status?.length) params.append("allocation_status", filters.allocation_status.join(","));
      if (filters.status?.length) params.append("status", filters.status.join(","));
      if (filters.search) params.append("search", filters.search);
      if (filters.payment_type?.length) params.append("payment_type", filters.payment_type.join(","));
      if (filters.city?.length) params.append("city", filters.city.join(","));
      
      // Fabric Type multi-select filter (all users)
      if (filters.filter_fabric_types?.length > 0) {
        filters.filter_fabric_types.forEach(ft => params.append("filter_fabric_type", ft));
      }
      // Region multi-select filter (all users)
      if (filters.filter_regions?.length > 0) {
        filters.filter_regions.forEach(r => params.append("filter_region", r));
      }
      // ICP Score range filter — user-defined min/max (0-10, decimals allowed)
      if (filters.score_min !== "" && filters.score_min !== null && filters.score_min !== undefined) {
        params.append("score_min", String(filters.score_min));
      }
      if (filters.score_max !== "" && filters.score_max !== null && filters.score_max !== undefined) {
        params.append("score_max", String(filters.score_max));
      }
      if (filters.order_timeline?.length) {
        params.append("order_timeline", filters.order_timeline.join(","));
      }

      // Smart filters (admin only)
      if (['admin','super_admin'].includes(currentUser?.role)) {
        if (filters.assigned_to?.includes('unassigned')) {
          params.append("unassigned", "true");
        }
        const agentIds = (filters.assigned_to || []).filter(a => a !== 'unassigned');
        if (agentIds.length > 0) {
          agentIds.forEach(id => params.append("assigned_to", id));
        }
        if (filters.primary_fabric_material?.length) params.append("primary_fabric_material", filters.primary_fabric_material.join(","));
        if (filters.unallocated_days) params.append("unallocated_days", filters.unallocated_days);
        if (filters.last_allocation_date) params.append("last_allocation_date", filters.last_allocation_date);
        if (filters.last_call_date) params.append("last_call_date", filters.last_call_date);
        if (filters.flagged) params.append("flagged", filters.flagged);
        if (filters.order_fabric_type) params.append("order_fabric_type", filters.order_fabric_type);
        if (filters.order_category) params.append("order_category", filters.order_category);
        if (filters.last_order_date) params.append("last_order_date", filters.last_order_date);
      }

      const response = await axios.get(`${API}/leads?${params.toString()}`);
      const loadedLeads = response.data.leads || [];
      setLeads(loadedLeads);
      
      // Collect distinct cities from all leads for city filter
      const cities = [...new Set(loadedLeads.map(l => l.city).filter(Boolean))].sort();
      if (cities.length > 0) setDistinctCities(prev => {
        const merged = [...new Set([...prev, ...cities])].sort();
        return merged;
      });
      setPagination(response.data.pagination || {
        page: 1,
        limit: 50,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setIsLoading(false);
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchLeads(page);
      setSelectedLeads([]); // Clear selection on page change
    }
  };

  const goToNextPage = () => {
    if (pagination.has_next) {
      goToPage(pagination.page + 1);
    }
  };

  const goToPrevPage = () => {
    if (pagination.has_prev) {
      goToPage(pagination.page - 1);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchAgents();
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (currentUser !== null) {
      // Reset to page 1 when filters change
      fetchLeads(1);
    }
  }, [filters, currentUser]);

  // Mass allocate leads to agent
  const handleMassAllocate = async () => {
    if (!allocateToAgent || selectedLeads.length === 0) {
      toast.error("Please select leads and an agent");
      return;
    }

    const selectedAgent = agents.find(a => a.id === allocateToAgent);
    if (!selectedAgent) {
      toast.error("Agent not found");
      return;
    }

    setIsAllocating(true);
    try {
      const response = await axios.post(`${API}/leads/mass-allocate`, {
        lead_ids: selectedLeads,
        agent_id: allocateToAgent,
        agent_name: selectedAgent.name
      });
      toast.success(response.data.message);
      setShowMassAllocate(false);
      setSelectedLeads([]);
      setAllocateToAgent("");
      fetchLeads();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns a structured payload when the 7-day Pick Pool lock fires.
      // Super admins get an override dialog; everyone else just sees the toast.
      if (detail && typeof detail === "object" && detail.code === "PICK_POOL_LOCK") {
        if (detail.can_override && currentUser?.role === "super_admin") {
          setPickPoolOverride({
            open: true,
            message: detail.message || "",
            blocked: detail.blocked_leads || [],
            reason: "",
          });
        } else {
          toast.error(detail.message || "Cannot reallocate Pick Pool lead yet");
        }
      } else {
        toast.error((typeof detail === "string" ? detail : null) || "Failed to allocate leads");
      }
    } finally {
      setIsAllocating(false);
    }
  };

  // Super-admin Pick-Pool lock override dialog
  const [pickPoolOverride, setPickPoolOverride] = useState({ open: false, message: "", blocked: [], reason: "" });
  const [isOverrideSubmitting, setIsOverrideSubmitting] = useState(false);

  const submitPickPoolOverride = async () => {
    const reason = (pickPoolOverride.reason || "").trim();
    if (reason.length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    const selectedAgent = agents.find(a => a.id === allocateToAgent);
    if (!selectedAgent) {
      toast.error("Agent not found");
      return;
    }
    setIsOverrideSubmitting(true);
    try {
      const response = await axios.post(`${API}/leads/mass-allocate`, {
        lead_ids: selectedLeads,
        agent_id: allocateToAgent,
        agent_name: selectedAgent.name,
        override_reason: reason,
      });
      toast.success(response.data.message);
      setPickPoolOverride({ open: false, message: "", blocked: [], reason: "" });
      setShowMassAllocate(false);
      setSelectedLeads([]);
      setAllocateToAgent("");
      fetchLeads();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Override failed");
    } finally {
      setIsOverrideSubmitting(false);
    }
  };

  const handleMassDelete = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Please select leads to delete");
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(`${API}/leads/bulk-delete`, {
        lead_ids: selectedLeads
      });
      toast.success(response.data.message);
      setShowMassDelete(false);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete leads");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportLeads = async () => {
    // Validate filter inputs
    if (exportFilter.type !== "none") {
      if (!exportFilter.start && !exportFilter.end) {
        toast.error("Pick at least a Start or End date, or choose 'No date filter'");
        return;
      }
      if (exportFilter.start && exportFilter.end && exportFilter.start > exportFilter.end) {
        toast.error("Start date must be on or before End date");
        return;
      }
    }
    setExportDownloading(true);
    const toastId = toast.loading('Preparing export... this may take a minute');
    try {
      const params = new URLSearchParams();
      if (exportFilter.type !== "none") {
        params.append("lead_date_filter", exportFilter.type);
        if (exportFilter.start) params.append("lead_date_start", exportFilter.start);
        if (exportFilter.end) params.append("lead_date_end", exportFilter.end);
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/leads/export-excel${qs}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = exportFilter.type === "none"
        ? "all"
        : `${exportFilter.type}_${exportFilter.start || "start"}_to_${exportFilter.end || "end"}`;
      link.setAttribute('download', `locofast_leads_${suffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded', { id: toastId });
      setShowExportDialog(false);
    } catch (err) {
      console.error('Export failed:', err);
      let detail = '';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          try {
            const parsed = JSON.parse(text);
            detail = parsed.detail || parsed.message || text;
          } catch {
            detail = text;
          }
        } else {
          detail = err.response?.data?.detail || err.response?.data?.message || '';
        }
      } catch {
        detail = '';
      }
      let msg;
      if (detail) msg = `Export failed: ${detail}`;
      else if (err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout'))
        msg = 'Export timed out. Please try again — large datasets can take a minute.';
      else if (err.message === 'Network Error')
        msg = 'Export failed: Network error. Check your connection and retry.';
      else if (err.response?.status)
        msg = `Export failed (HTTP ${err.response.status}). Please retry in a moment.`;
      else
        msg = 'Export failed. Please retry — if it keeps failing, contact admin.';
      toast.error(msg, { id: toastId, duration: 8000 });
    } finally {
      setExportDownloading(false);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields for manual lead creation
    if (!newLead.phone || !newLead.phone.trim()) {
      toast.error("Phone number is required for manual lead creation");
      return;
    }
    if (!newLead.contact_person || !newLead.contact_person.trim()) {
      toast.error("Contact person is required for manual lead creation");
      return;
    }
    if (newLead.region !== "Bangladesh" && (!newLead.company_name || !newLead.company_name.trim())) {
      toast.error("Company name is required");
      return;
    }
    if (!newLead.data_source) {
      toast.error("Data source is required");
      return;
    }
    if (newLead.region !== "Bangladesh") {
      if (!newLead.gst_turnover) {
        toast.error("GST Turnover is mandatory");
        return;
      }
    }
    if (!newLead.email || !newLead.email.trim()) {
      toast.error("Email is mandatory");
      return;
    }
    if (newLead.region !== "Bangladesh") {
      if (!newLead.has_website) {
        toast.error("Has Website (Yes/No) is mandatory");
        return;
      }
      if (newLead.has_website === "Yes" && (!newLead.website || !newLead.website.trim())) {
        toast.error("Website URL is mandatory when Has Website is 'Yes'");
        return;
      }
      if (!newLead.business_type) {
        toast.error("Business Type is mandatory");
        return;
      }
    }
    // Fabric Category mandatory for "Query exists" / "Meta"
    if ((newLead.data_source === "Query exists" || newLead.data_source === "Meta") &&
        !(newLead.fabric_category || newLead.product_category)) {
      toast.error(`Category is mandatory for '${newLead.data_source}'`);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Sync name field from contact_person (name is the primary contact person field)
      // Also sync fabric_category from product_category (UI labels it as "Category")
      const leadData = {
        ...newLead,
        name: newLead.contact_person || newLead.name,  // Use contact_person as name
        fabric_category: newLead.fabric_category || newLead.product_category || ""
      };
      const response = await axios.post(`${API}/leads`, leadData);
      const leadId = response.data.lead_id;
      
      // If sample image was selected, upload it
      if (sampleFile && leadId) {
        const formData = new FormData();
        formData.append('file', sampleFile);
        try {
          await axios.post(`${API}/leads/${leadId}/upload-sample`, formData);
        } catch (uploadError) {
          console.error("Failed to upload sample image:", uploadError);
          toast.warning("Lead created but sample image upload failed");
        }
      }
      
      toast.success("Lead created successfully");
      setShowAddDialog(false);
      setShowSecondaryContactInAdd(false);
      setSampleFile(null);
      setNewLead({
        name: "",
        phone: "",
        email: "",
        company_name: "",
        contact_person: "",
        contact_designation: "",
        contact_linkedin: "",
        contact_2_name: "",
        contact_2_designation: "",
        contact_2_phone: "",
        contact_2_email: "",
        contact_2_linkedin: "",
        gst_number: "",
        gst_registration_date: "",
        gst_turnover: "",
        website: "",
        has_website: "",
        business_type: "",
        data_source: "",
        quantity: "",
        quantity_unit: "",
        address: "",
        city: "",
        state: "",
        region: "India",
        customer_type: "",
        notes: "",
        product_category: "",
        fabric_category: "",
        fabric_type: "",
        primary_material: "",
        fabric_subcategory: "",
        pattern: "",
        color: "",
        finish: "",
        wash: "",
        gsm: "",
        width_inches: "",
        epi: "",
        ppi: "",
        material_1: "",
        material_1_percentage: "",
        material_2: "",
        material_2_percentage: "",
        material_3: "",
        material_3_percentage: "",
        material_4: "",
        material_4_percentage: "",
        warp_count: "",
        weft_count: "",
        weft_shrinkage: "",
        stretch: "",
        sample_shared: false,
      });
      fetchLeads(1);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors
        toast.error(detail.map(e => e.msg).join(', ') || "Validation error");
      } else {
        toast.error(detail || "Failed to create lead");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category change - reset category-specific fields
  const handleCategoryChange = (category) => {
    setNewLead({
      ...newLead,
      fabric_category: category,
      fabric_type: "",
      fabric_quality: "",
      fabric_specification: "",
      quantity: ""
    });
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleClickToCall = async (lead) => {
    try {
      const response = await axios.post(`${API}/calls/click-to-call`, {
        lead_id: lead.id,
        phone_number: lead.phone
      });
      toast.success(response.data.message);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(', ') || "Failed to initiate call");
      } else {
        toast.error(detail || "Failed to initiate call");
      }
    }
  };

  const handleManualCallLog = async () => {
    if (!manualCallLead) return;
    // Validate: connected calls must have duration > 0
    const connectedStatuses = ["connected", "requirement_shared"];
    if (connectedStatuses.includes(manualCallData.status)) {
      const totalSec = (parseInt(manualCallData.duration_minutes) || 0) * 60 + (parseInt(manualCallData.duration_seconds_input) || 0);
      if (totalSec === 0) {
        toast.error("Duration cannot be zero for connected calls");
        return;
      }
    }
    // Mandatory follow-up for agents — removed per latest product call (now optional).
    // const isAgent = currentUser?.role === 'user';
    // if (isAgent && (!manualCallData.followup_description || manualCallData.followup_description.trim().length < 3)) {
    //   toast.error("A follow-up is mandatory when logging a call. Please add a description.");
    //   return;
    // }
    try {
      const response = await axios.post(`${API}/calls/manual-log`, {
        lead_id: manualCallLead.id,
        ...manualCallData
      });
      const callId = response.data.call_id;

      // Upload screenshot if selected
      if (callScreenshotFile && callId) {
        setIsUploadingCallScreenshot(true);
        const formData = new FormData();
        formData.append('file', callScreenshotFile);
        try {
          await axios.post(`${API}/calls/${callId}/upload-screenshot`, formData);
        } catch (uploadErr) {
          console.error("Screenshot upload failed:", uploadErr);
          toast.warning("Call logged but screenshot upload failed");
        } finally {
          setIsUploadingCallScreenshot(false);
        }
      }

      toast.success(response.data?.followup_id ? "Call logged + follow-up scheduled" : "Call logged successfully");
      setShowManualCallLog(false);
      setManualCallLead(null);
      setCallScreenshotFile(null);
      const _resetLeadsCallDate = new Date().toISOString().split('T')[0];
      setManualCallData({
        call_date: _resetLeadsCallDate,
        call_time: new Date().toTimeString().slice(0, 5),
        status: "connected",
        duration_minutes: "",
        duration_seconds_input: "",
        notes: "",
        followup_description: "",
        followup_date: addDaysToDate(_resetLeadsCallDate, 7),
        followup_time: "10:00",
        followup_priority: "medium",
        followup_date_touched: false,
      });
      fetchLeads();
    } catch (error) {
      console.error("Manual call log failed:", error);
      const d = error.response?.data?.detail;
      let msg;
      if (typeof d === "string") {
        msg = d;
      } else if (Array.isArray(d)) {
        msg = d.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; ');
      } else if (d && typeof d === "object") {
        msg = d.msg || JSON.stringify(d);
      } else if (error.response?.data?.message) {
        msg = error.response.data.message;
      } else if (error.response?.status) {
        msg = `Failed to log call (HTTP ${error.response.status} ${error.response.statusText || ""})`;
      } else if (error.message) {
        msg = `Failed to log call: ${error.message}`;
      } else {
        msg = "Failed to log call";
      }
      toast.error(msg, { duration: 6000 });
    }
  };

  // Bulk selection handlers
  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  // Compute status based on activity
  const getComputedStatus = (lead) => {
    if (lead.pick_pool_ready) return "pick_pool";
    const hasCallsMade = lead.call_count > 0 || lead.last_call_date;
    const hasLfb = lead.lfb_created === true;
    
    if (hasLfb) return "contacted_lfb";
    if (hasCallsMade) return "contacted";
    return "new";
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700 border-blue-200",
      contacted: "bg-yellow-100 text-yellow-700 border-yellow-200",
      contacted_lfb: "bg-green-100 text-green-700 border-green-200",
      pick_pool: "bg-purple-100 text-purple-700 border-purple-200"
    };
    return colors[status] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const formatStatus = (status) => {
    const labels = {
      new: "New",
      contacted: "Contacted",
      contacted_lfb: "Contacted & LFB",
      pick_pool: "Pick Pool"
    };
    return labels[status] || status;
  };

  const getCallStatusStyle = (status) => {
    const styles = {
      connected: "bg-emerald-50 text-emerald-700 border-emerald-200",
      requirement_shared: "bg-green-50 text-green-700 border-green-200",
      call_again_later: "bg-amber-50 text-amber-700 border-amber-200",
      no_requirement: "bg-slate-100 text-slate-600 border-slate-200",
      not_connected: "bg-red-50 text-red-700 border-red-200",
      not_relevant: "bg-rose-50 text-rose-700 border-rose-200",
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      busy: "bg-orange-50 text-orange-700 border-orange-200",
      no_answer: "bg-slate-50 text-slate-600 border-slate-200",
      failed: "bg-red-50 text-red-700 border-red-200",
    };
    return styles[status] || "bg-slate-50 text-slate-600 border-slate-200";
  };

  const formatCallStatus = (status) => {
    const labels = {
      connected: "Connected",
      requirement_shared: "Req Shared",
      call_again_later: "Call Later",
      no_requirement: "No Req",
      not_connected: "Not Connected",
      not_relevant: "Not Relevant",
      completed: "Completed",
      busy: "Busy",
      no_answer: "No Answer",
      failed: "Failed",
    };
    return labels[status] || status?.replace('_', ' ');
  };

  const clearFilters = () => {
    setFilters({
      fabric_category: [],
      data_source: [],
      allocation_status: [],
      status: [],
      search: "",
      payment_type: [],
      city: [],
      last_allocation_date: "",
      last_call_date: "",
      primary_fabric_material: [],
      unallocated_days: "",
      assigned_to: [],
      flagged: "",
      order_fabric_type: "",
      order_category: "",
      last_order_date: "",
      filter_fabric_types: [],
      filter_regions: [],
      score_min: "",
      score_max: "",
      order_timeline: [],
      gst_turnover: [],
    });
  };

  const hasActiveFilters = (filters.fabric_category?.length > 0) || (filters.data_source?.length > 0) ||
    (filters.allocation_status?.length > 0) || (filters.status?.length > 0) ||
    (filters.payment_type?.length > 0) || (filters.city?.length > 0) ||
    filters.last_allocation_date || filters.last_call_date || (filters.primary_fabric_material?.length > 0) ||
    filters.unallocated_days || (filters.assigned_to?.length > 0) || filters.flagged ||
    filters.order_fabric_type || filters.order_category || filters.last_order_date ||
    (filters.gst_turnover?.length > 0) ||
    (filters.filter_fabric_types?.length > 0) || (filters.filter_regions?.length > 0) ||
    filters.score_min !== "" || filters.score_max !== "" || (filters.order_timeline?.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Leads
          </h1>
          <p className="text-slate-500 mt-1">{pagination.total_count} total leads</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Bulk Actions */}
          {selectedLeads.length > 0 && (
            <div className="flex items-center gap-2 mr-2">
              <span className="text-sm text-slate-600 bg-blue-50 px-2 py-1 rounded">
                {selectedLeads.length} selected
              </span>
              {['admin','super_admin'].includes(currentUser?.role) && (
                <>
                  <Button 
                    size="sm"
                    onClick={() => setShowMassAllocate(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="mass-allocate-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Allocate
                  </Button>
                  <Button 
                    size="sm"
                    variant="destructive"
                    onClick={() => setShowMassDelete(true)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="mass-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedLeads([])}
                className="text-slate-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          {/* File Import & Bulk Update — admins get Import + Bulk; agents get Bulk only */}
          <Button 
            variant="outline" 
            onClick={() => setShowCSVImport(true)}
            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            data-testid="import-csv-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {['admin','super_admin'].includes(currentUser?.role) ? 'Upload Data' : 'Bulk Update'}
          </Button>
          {/* Remove Duplicates - Super Admin only */}
          {currentUser?.email === 'admin@locofast.com' && (
            <Button
              variant="outline"
              onClick={handleOpenDedupDialog}
              className="border-rose-500 text-rose-600 hover:bg-rose-50"
              data-testid="remove-duplicates-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Duplicates
            </Button>
          )}
          {/* Export Button - Restricted to specific admin */}
          {currentUser?.email && ['kaushlendra.singh@locofast.com'].includes((currentUser.email || '').trim().toLowerCase()) && (
            <Button
              variant="outline"
              onClick={() => setShowExportDialog(true)}
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
              data-testid="export-leads-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
          )}

          {/* Leads Export Dialog */}
          <Dialog open={showExportDialog} onOpenChange={(v) => { if (!exportDownloading) setShowExportDialog(v); }}>
            <DialogContent className="max-w-md" data-testid="export-leads-dialog">
              <DialogHeader>
                <DialogTitle>Export Leads to Excel</DialogTitle>
                <DialogDescription>
                  Optionally narrow the export by a date range. Pick which date field to filter on.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Filter by</Label>
                  <div className="space-y-2">
                    {[
                      { v: "none", label: "No date filter (download all)" },
                      { v: "created", label: "Lead Created Date" },
                      { v: "activity", label: "Last Activity Date (last call or last touch)" },
                    ].map(opt => (
                      <label key={opt.v} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`export-filter-${opt.v}`}>
                        <input
                          type="radio"
                          name="export-filter-type"
                          value={opt.v}
                          checked={exportFilter.type === opt.v}
                          onChange={() => setExportFilter(f => ({ ...f, type: opt.v }))}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-slate-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {exportFilter.type !== "none" && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                    <div>
                      <Label className="text-xs">From</Label>
                      <Input
                        type="date"
                        value={exportFilter.start}
                        onChange={(e) => setExportFilter(f => ({ ...f, start: e.target.value }))}
                        data-testid="export-leads-start"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">To</Label>
                      <Input
                        type="date"
                        value={exportFilter.end}
                        onChange={(e) => setExportFilter(f => ({ ...f, end: e.target.value }))}
                        data-testid="export-leads-end"
                      />
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowExportDialog(false)} disabled={exportDownloading}>Cancel</Button>
                <Button
                  onClick={handleExportLeads}
                  disabled={exportDownloading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="export-leads-confirm"
                >
                  {exportDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white btn-press" data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>
                Fields marked with <span className="text-red-500">*</span> are required. 
                Fields marked with <span className="text-green-600 font-semibold">★</span> are required for Pick Pool.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateLead} className="space-y-6 mt-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* Contact Information */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name {newLead.region !== "Bangladesh" && <span className="text-red-500">*</span>}</Label>
                    <Input
                      id="company"
                      value={newLead.company_name}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Set both company_name and name (name is the primary identifier)
                        setNewLead(prev => ({ ...prev, company_name: value, name: value }));
                      }}
                      required={newLead.region !== "Bangladesh"}
                      data-testid="lead-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_person">Contact Person <span className="text-red-500">*</span></Label>
                    <Input
                      id="contact_person"
                      value={newLead.contact_person}
                      onChange={(e) => setNewLead(prev => ({ ...prev, contact_person: e.target.value }))}
                      placeholder="Required for manual entry"
                      data-testid="lead-contact-person-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_designation">Designation</Label>
                    <Input
                      id="contact_designation"
                      value={newLead.contact_designation}
                      onChange={(e) => setNewLead(prev => ({ ...prev, contact_designation: e.target.value }))}
                      placeholder="e.g., Procurement Head"
                      data-testid="lead-contact-designation-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_linkedin">LinkedIn URL</Label>
                    <Input
                      id="contact_linkedin"
                      value={newLead.contact_linkedin}
                      onChange={(e) => setNewLead(prev => ({ ...prev, contact_linkedin: e.target.value }))}
                      placeholder="https://linkedin.com/in/..."
                      data-testid="lead-contact-linkedin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Region *</Label>
                    <Select
                      value={newLead.region}
                      onValueChange={(value) => setNewLead(prev => ({ ...prev, region: value, data_source: "" }))}
                      required
                    >
                      <SelectTrigger data-testid="lead-region-select">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="India">India</SelectItem>
                        <SelectItem value="Bangladesh">Bangladesh</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Data Source *</Label>
                    <Select
                      value={newLead.data_source}
                      onValueChange={(value) => setNewLead(prev => ({ ...prev, data_source: value, sub_data_source: value !== "E-Commerce Data" ? "" : prev.sub_data_source }))}
                      required
                    >
                      <SelectTrigger data-testid="lead-data-source-select">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {(newLead.region === "Bangladesh" ? BANGLADESH_DATA_SOURCES : INDIA_DATA_SOURCES).map((ds) => (
                          <SelectItem key={ds} value={ds}>
                            {ds}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {newLead.data_source === "E-Commerce Data" && (
                    <div className="space-y-2">
                      <Label>Platform *</Label>
                      <Select
                        value={newLead.sub_data_source}
                        onValueChange={(value) => setNewLead(prev => ({ ...prev, sub_data_source: value }))}
                      >
                        <SelectTrigger data-testid="lead-sub-source-select">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          {ECOMMERCE_SUB_SOURCES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
                    <Input
                      id="phone"
                      value={newLead.phone}
                      onChange={(e) => setNewLead(prev => ({ ...prev, phone: e.target.value }))}
                      data-testid="lead-phone-input"
                      placeholder={newLead.region === "Bangladesh" ? "+88XXXXXXXXXX" : "Phone number"}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead(prev => ({ ...prev, email: e.target.value }))}
                      data-testid="lead-email-input"
                      required
                    />
                  </div>
                  {newLead.region !== "Bangladesh" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="gst">GST Number <span className="text-green-600">★</span></Label>
                        <div className="flex gap-2">
                          <Input
                            id="gst"
                            value={newLead.gst_number}
                            onChange={(e) => {
                              setNewLead(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }));
                              setGstVerified(null);
                            }}
                            placeholder="e.g., 22AAAAA0000A1Z5"
                            className="font-mono flex-1"
                            data-testid="lead-gst-input"
                            maxLength={15}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant={gstVerified?.active ? "outline" : "default"}
                            className={gstVerified?.active ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-blue-600 hover:bg-blue-700 text-white"}
                            onClick={handleVerifyGST}
                            disabled={gstVerifying || !newLead.gst_number?.trim()}
                            data-testid="gst-verify-btn"
                          >
                            {gstVerifying ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : gstVerified?.active ? (
                              <><ShieldCheck className="w-4 h-4 mr-1" />Verified</>
                            ) : (
                              <><ShieldCheck className="w-4 h-4 mr-1" />Verify</>
                            )}
                          </Button>
                        </div>
                        {gstVerified && !gstVerified.active && (
                          <p className="text-xs text-red-600 font-medium" data-testid="gst-status-error">GST Status: {gstVerified.status}</p>
                        )}
                        {gstVerified?.active && (
                          <p className="text-xs text-emerald-600 font-medium" data-testid="gst-status-ok">GST Verified — Active ({gstVerified.constitution})</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gst_reg_date">GST Registration Date <span className="text-red-500">*</span></Label>
                        <Input
                          id="gst_reg_date"
                          type="date"
                          value={newLead.gst_registration_date}
                          onChange={(e) => setNewLead(prev => ({ ...prev, gst_registration_date: e.target.value }))}
                          data-testid="lead-gst-reg-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>GST Turnover <span className="text-red-500">*</span></Label>
                        <Select
                          value={newLead.gst_turnover}
                          onValueChange={(value) => setNewLead(prev => ({ ...prev, gst_turnover: value }))}
                        >
                          <SelectTrigger data-testid="lead-gst-turnover-select">
                            <SelectValue placeholder="Select turnover range" />
                          </SelectTrigger>
                          <SelectContent>
                            {["0-40L", "40L-1.5Cr", "1.5Cr-5Cr", "5Cr-25Cr", "25Cr-100Cr", "100Cr-500Cr", "500Cr+"].map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  {newLead.region === "Bangladesh" && (
                    <div className="space-y-2">
                      <Label htmlFor="bin_number">BIN Number</Label>
                      <Input
                        id="bin_number"
                        value={newLead.bin_number || ""}
                        onChange={(e) => setNewLead(prev => ({ ...prev, bin_number: e.target.value }))}
                        placeholder="BIN number (optional at creation)"
                        data-testid="lead-bin-number-input"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Has Website {newLead.region !== "Bangladesh" && <span className="text-red-500">*</span>}</Label>
                    <Select
                      value={newLead.has_website}
                      onValueChange={(value) => {
                        setNewLead(prev => ({ ...prev, has_website: value, website: value === "No" ? "" : prev.website }));
                      }}
                    >
                      <SelectTrigger data-testid="lead-has-website-select">
                        <SelectValue placeholder="Yes / No" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newLead.has_website === "Yes" && (
                  <div className="space-y-2">
                    <Label htmlFor="website">Website <span className="text-red-500">*</span></Label>
                    <Input
                      id="website"
                      value={newLead.website}
                      onChange={(e) => setNewLead(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="e.g., www.company.com"
                      data-testid="lead-website-input"
                    />
                  </div>
                  )}
                  <div className="space-y-2">
                    <Label>Business Type {newLead.region !== "Bangladesh" && <span className="text-red-500">*</span>}</Label>
                    <Select
                      value={newLead.business_type}
                      onValueChange={(value) => setNewLead(prev => ({ ...prev, business_type: value }))}
                    >
                      <SelectTrigger data-testid="lead-business-type-select">
                        <SelectValue placeholder="Select business type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(newLead.region === "Bangladesh"
                          ? ["Garment Manufacturer", "Buying House", "Fabric Trader", "Processing Mill"]
                          : ["Private Limited", "Partnership Firm", "LLP", "Proprietorship", "Limited/Public"]
                        ).map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Customer Type</Label>
                    <Select
                      value={newLead.customer_type}
                      onValueChange={(value) => setNewLead(prev => ({ ...prev, customer_type: value }))}
                    >
                      <SelectTrigger data-testid="lead-customer-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownOptions?.customer_types || ["Trader", "Manufacturer", "Brand"]).map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Type</Label>
                    <Select
                      value={newLead.payment_type || ""}
                      onValueChange={(value) => setNewLead(prev => ({ ...prev, payment_type: value }))}
                    >
                      <SelectTrigger data-testid="lead-payment-type-select">
                        <SelectValue placeholder="Cash / Credit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Credit">Credit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City <span className="text-green-600">★</span></Label>
                    {newLead.region === "Bangladesh" ? (
                      <Select
                        value={newLead.city || ""}
                        onValueChange={(value) => setNewLead(prev => ({ ...prev, city: value }))}
                      >
                        <SelectTrigger data-testid="lead-city-input">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          {BANGLADESH_CITIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="city"
                        value={newLead.city}
                        onChange={(e) => setNewLead(prev => ({ ...prev, city: e.target.value }))}
                        data-testid="lead-city-input"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>State / Division</Label>
                    <Select
                      value={newLead.state || ""}
                      onValueChange={(value) => setNewLead(prev => ({ ...prev, state: value }))}
                    >
                      <SelectTrigger data-testid="lead-state-select">
                        <SelectValue placeholder={newLead.region === "Bangladesh" ? "Select division" : "Select state"} />
                      </SelectTrigger>
                      <SelectContent>
                        {(newLead.region === "Bangladesh" ? BANGLADESH_DIVISIONS : INDIAN_STATES).map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={newLead.address}
                      onChange={(e) => setNewLead({ ...newLead, address: e.target.value })}
                      data-testid="lead-address-input"
                    />
                  </div>
                </div>

                {/* Secondary Contact - Collapsible */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowSecondaryContactInAdd(!showSecondaryContactInAdd)}
                    className="flex items-center justify-between w-full p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
                    data-testid="add-toggle-secondary-contact-btn"
                  >
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-semibold text-slate-700">Add Secondary Contact (optional)</span>
                    </div>
                    {showSecondaryContactInAdd ? (
                      <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                  </button>
                  {showSecondaryContactInAdd && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50/50 border border-slate-200 rounded-lg" data-testid="add-secondary-contact-section">
                      <div className="space-y-2">
                        <Label htmlFor="contact_2_name">Name</Label>
                        <Input
                          id="contact_2_name"
                          value={newLead.contact_2_name}
                          onChange={(e) => setNewLead(prev => ({ ...prev, contact_2_name: e.target.value }))}
                          data-testid="lead-contact-2-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_2_designation">Designation</Label>
                        <Input
                          id="contact_2_designation"
                          value={newLead.contact_2_designation}
                          onChange={(e) => setNewLead(prev => ({ ...prev, contact_2_designation: e.target.value }))}
                          placeholder="e.g., Sourcing Manager"
                          data-testid="lead-contact-2-designation-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_2_phone">Phone</Label>
                        <Input
                          id="contact_2_phone"
                          value={newLead.contact_2_phone}
                          onChange={(e) => setNewLead(prev => ({ ...prev, contact_2_phone: e.target.value }))}
                          data-testid="lead-contact-2-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_2_email">Email</Label>
                        <Input
                          id="contact_2_email"
                          type="email"
                          value={newLead.contact_2_email}
                          onChange={(e) => setNewLead(prev => ({ ...prev, contact_2_email: e.target.value }))}
                          data-testid="lead-contact-2-email-input"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="contact_2_linkedin">LinkedIn URL</Label>
                        <Input
                          id="contact_2_linkedin"
                          value={newLead.contact_2_linkedin}
                          onChange={(e) => setNewLead(prev => ({ ...prev, contact_2_linkedin: e.target.value }))}
                          placeholder="https://linkedin.com/in/..."
                          data-testid="lead-contact-2-linkedin-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Details */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">Product Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Category - Woven, Knits, Non Woven */}
                  <div className="space-y-2">
                    <Label>Category * <span className="text-green-600">★</span></Label>
                    <Select
                      value={newLead.product_category}
                      onValueChange={(value) => {
                        setNewLead({ 
                          ...newLead, 
                          product_category: value,
                          fabric_category: value,
                          fabric_type: "",
                          pattern: "",
                          color: "",
                          finish: "",
                          wash: ""
                        });
                      }}
                    >
                      <SelectTrigger data-testid="lead-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Fabric Type - Greige, Dyed, Printed, Denim */}
                  <div className="space-y-2">
                    <Label>Fabric Type <span className="text-green-600">★</span></Label>
                    <Select
                      value={newLead.fabric_type}
                      onValueChange={(value) => {
                        setNewLead({ 
                          ...newLead, 
                          fabric_type: value,
                          pattern: value !== "Printed" ? "" : newLead.pattern,
                          color: value !== "Dyed" ? "" : newLead.color
                        });
                      }}
                    >
                      <SelectTrigger data-testid="lead-fabric-type-select">
                        <SelectValue placeholder="Select fabric type" />
                      </SelectTrigger>
                      <SelectContent>
                        {FABRIC_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Primary Material - Cotton, Polyester, Viscose */}
                  <div className="space-y-2">
                    <Label>Primary Fabric Material <span className="text-green-600">★</span></Label>
                    <Select
                      value={newLead.primary_material}
                      onValueChange={(value) => setNewLead({ ...newLead, primary_material: value })}
                    >
                      <SelectTrigger data-testid="lead-primary-material-select">
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIMARY_MATERIALS.map((mat) => (
                          <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Fabric Subcategory - Free text */}
                  <div className="space-y-2">
                    <Label>Fabric Subcategory</Label>
                    <Input
                      value={newLead.fabric_subcategory}
                      onChange={(e) => setNewLead({ ...newLead, fabric_subcategory: e.target.value })}
                      placeholder="e.g., Twill, Satin, Jersey"
                      data-testid="lead-subcategory-input"
                    />
                  </div>
                  
                  {/* Pattern - ONLY for Printed */}
                  {newLead.fabric_type === "Printed" && (
                    <div className="space-y-2">
                      <Label>Pattern <span className="text-green-600">★</span></Label>
                      <Select
                        value={newLead.pattern}
                        onValueChange={(value) => setNewLead({ ...newLead, pattern: value })}
                      >
                        <SelectTrigger data-testid="lead-pattern-select">
                          <SelectValue placeholder="Select pattern" />
                        </SelectTrigger>
                        <SelectContent>
                          {(dropdownOptions?.patterns || ["Stripes", "Checks", "Floral", "Abstract", "Geometric", "Solid", "Others"]).map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Color - for Dyed and Printed only (NOT for Greige or Denim) */}
                  {(newLead.fabric_type === "Dyed" || newLead.fabric_type === "Printed") && (
                    <div className="space-y-2">
                      <Label>Color <span className="text-green-600">★</span></Label>
                      <Select
                        value={newLead.color}
                        onValueChange={(value) => setNewLead({ ...newLead, color: value })}
                      >
                        <SelectTrigger data-testid="lead-color-select">
                          <SelectValue placeholder="Select color" />
                        </SelectTrigger>
                        <SelectContent>
                          {(dropdownOptions?.colors || ["Any", "White", "Black", "Navy", "Red", "Blue", "Green", "Yellow", "Pink", "Grey", "Brown", "Others"]).map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Finish - NOT for Greige or Denim */}
                  {newLead.fabric_type !== "Greige" && newLead.fabric_type !== "Denim" && newLead.fabric_type && (
                    <div className="space-y-2">
                      <Label>Finish <span className="text-green-600">★</span></Label>
                      <Select
                        value={newLead.finish}
                        onValueChange={(value) => setNewLead({ ...newLead, finish: value })}
                      >
                        <SelectTrigger data-testid="lead-finish-select">
                          <SelectValue placeholder="Select finish" />
                        </SelectTrigger>
                        <SelectContent>
                          {FINISH_OPTIONS.map((f) => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Wash - ONLY for Denim */}
                  {newLead.fabric_type === "Denim" && (
                    <div className="space-y-2">
                      <Label>Wash <span className="text-green-600">★</span></Label>
                      <Select
                        value={newLead.wash}
                        onValueChange={(value) => setNewLead({ ...newLead, wash: value })}
                      >
                        <SelectTrigger data-testid="lead-wash-select">
                          <SelectValue placeholder="Select wash" />
                        </SelectTrigger>
                        <SelectContent>
                          {WASH_OPTIONS.map((w) => (
                            <SelectItem key={w} value={w}>{w}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* GSM - Dropdown (multiples of 5) / OZ for Denim */}
                  {newLead.fabric_type === "Denim" ? (
                    <div className="space-y-2">
                      <Label>Weight (oz) <span className="text-green-600">★</span></Label>
                      <Select
                        value={newLead.gsm}
                        onValueChange={(value) => setNewLead({ ...newLead, gsm: value })}
                      >
                        <SelectTrigger data-testid="lead-oz-select">
                          <SelectValue placeholder="Select oz" />
                        </SelectTrigger>
                        <SelectContent>
                          {DENIM_OZ_OPTIONS.map((oz) => (
                            <SelectItem key={oz} value={oz}>{oz} oz</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>GSM <span className="text-green-600">★</span></Label>
                      <Select
                        value={newLead.gsm}
                        onValueChange={(value) => setNewLead({ ...newLead, gsm: value })}
                      >
                        <SelectTrigger data-testid="lead-gsm-select">
                          <SelectValue placeholder="Select GSM" />
                        </SelectTrigger>
                        <SelectContent>
                          {GSM_OPTIONS.map((gsm) => (
                            <SelectItem key={gsm} value={String(gsm)}>{gsm}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Width - Dropdown */}
                  <div className="space-y-2">
                    <Label>Width (inches) <span className="text-green-600">★</span></Label>
                    <Select
                      value={newLead.width_inches}
                      onValueChange={(value) => setNewLead({ ...newLead, width_inches: value })}
                    >
                      <SelectTrigger data-testid="lead-width-select">
                        <SelectValue placeholder="Select width" />
                      </SelectTrigger>
                      <SelectContent>
                        {WIDTH_OPTIONS.map((w) => (
                          <SelectItem key={w} value={String(w)}>{w}"</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Fabric Construction - EPI/PPI */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">Fabric Construction</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>EPI (Ends Per Inch / Warp)</Label>
                    <Select
                      value={newLead.epi}
                      onValueChange={(value) => setNewLead({ ...newLead, epi: value })}
                    >
                      <SelectTrigger data-testid="lead-epi-select">
                        <SelectValue placeholder="Select EPI" />
                      </SelectTrigger>
                      <SelectContent>
                        {EPI_PPI_OPTIONS.map((v) => (
                          <SelectItem key={v} value={String(v)}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>PPI (Picks Per Inch / Weft)</Label>
                    <Select
                      value={newLead.ppi}
                      onValueChange={(value) => setNewLead({ ...newLead, ppi: value })}
                    >
                      <SelectTrigger data-testid="lead-ppi-select">
                        <SelectValue placeholder="Select PPI" />
                      </SelectTrigger>
                      <SelectContent>
                        {EPI_PPI_OPTIONS.map((v) => (
                          <SelectItem key={v} value={String(v)}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Composition - Up to 4 materials */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">
                  Composition <span className="text-green-600 text-sm">★</span>
                  {(() => {
                    const pct1 = parseInt(newLead.material_1_percentage) || 0;
                    const pct2 = parseInt(newLead.material_2_percentage) || 0;
                    const pct3 = parseInt(newLead.material_3_percentage) || 0;
                    const pct4 = parseInt(newLead.material_4_percentage) || 0;
                    const total = pct1 + pct2 + pct3 + pct4;
                    if (total > 0) {
                      return (
                        <span className={`ml-2 text-sm ${total === 100 ? 'text-green-600' : 'text-red-500'}`}>
                          (Total: {total}% {total === 100 ? '✓' : '- must equal 100%'})
                        </span>
                      );
                    }
                    return null;
                  })()}
                </h3>
                <p className="text-xs text-slate-500 mb-4">Select materials and percentages. Total must equal 100%.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Material 1 */}
                  <div className="space-y-2">
                    <Label>Material 1 <span className="text-green-600">★</span></Label>
                    <Select
                      value={newLead.material_1}
                      onValueChange={(value) => setNewLead({ ...newLead, material_1: value })}
                    >
                      <SelectTrigger data-testid="lead-material1-select">
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPOSITION_MATERIALS.map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Percentage {newLead.material_1 && <span className="text-red-500">*</span>}</Label>
                    <Select
                      value={newLead.material_1_percentage}
                      onValueChange={(value) => setNewLead({ ...newLead, material_1_percentage: value })}
                      disabled={!newLead.material_1}
                    >
                      <SelectTrigger data-testid="lead-material1-pct-select">
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERCENTAGE_OPTIONS.map(p => (
                          <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Material 2 */}
                  <div className="space-y-2">
                    <Label>Material 2</Label>
                    <Select
                      value={newLead.material_2}
                      onValueChange={(value) => setNewLead({ ...newLead, material_2: value })}
                    >
                      <SelectTrigger data-testid="lead-material2-select">
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPOSITION_MATERIALS.map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Percentage {newLead.material_2 && <span className="text-red-500">*</span>}</Label>
                    <Select
                      value={newLead.material_2_percentage}
                      onValueChange={(value) => setNewLead({ ...newLead, material_2_percentage: value })}
                      disabled={!newLead.material_2}
                    >
                      <SelectTrigger data-testid="lead-material2-pct-select">
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERCENTAGE_OPTIONS.map(p => (
                          <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Material 3 */}
                  <div className="space-y-2">
                    <Label>Material 3</Label>
                    <Select
                      value={newLead.material_3}
                      onValueChange={(value) => setNewLead({ ...newLead, material_3: value })}
                    >
                      <SelectTrigger data-testid="lead-material3-select">
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPOSITION_MATERIALS.map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Percentage {newLead.material_3 && <span className="text-red-500">*</span>}</Label>
                    <Select
                      value={newLead.material_3_percentage}
                      onValueChange={(value) => setNewLead({ ...newLead, material_3_percentage: value })}
                      disabled={!newLead.material_3}
                    >
                      <SelectTrigger data-testid="lead-material3-pct-select">
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERCENTAGE_OPTIONS.map(p => (
                          <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Material 4 */}
                  <div className="space-y-2">
                    <Label>Material 4</Label>
                    <Select
                      value={newLead.material_4}
                      onValueChange={(value) => setNewLead({ ...newLead, material_4: value })}
                    >
                      <SelectTrigger data-testid="lead-material4-select">
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPOSITION_MATERIALS.map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Percentage {newLead.material_4 && <span className="text-red-500">*</span>}</Label>
                    <Select
                      value={newLead.material_4_percentage}
                      onValueChange={(value) => setNewLead({ ...newLead, material_4_percentage: value })}
                      disabled={!newLead.material_4}
                    >
                      <SelectTrigger data-testid="lead-material4-pct-select">
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERCENTAGE_OPTIONS.map(p => (
                          <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Yarn Count - Denier for Denim and Polyester, regular for others */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">
                  Yarn Count {(newLead.fabric_type === "Denim" || newLead.material_1 === "Polyester") && <span className="text-xs text-slate-500">(in Denier)</span>}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(newLead.fabric_type === "Denim" || newLead.material_1 === "Polyester") ? (
                    <>
                      <div className="space-y-2">
                        <Label>Warp Count (Denier)</Label>
                        <Select
                          value={newLead.warp_count}
                          onValueChange={(value) => setNewLead({ ...newLead, warp_count: value })}
                        >
                          <SelectTrigger data-testid="lead-warp-denier-select">
                            <SelectValue placeholder="Select denier" />
                          </SelectTrigger>
                          <SelectContent>
                            {DENIER_OPTIONS.map((d) => (
                              <SelectItem key={d} value={d}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Weft Count (Denier)</Label>
                        <Select
                          value={newLead.weft_count}
                          onValueChange={(value) => setNewLead({ ...newLead, weft_count: value })}
                        >
                          <SelectTrigger data-testid="lead-weft-denier-select">
                            <SelectValue placeholder="Select denier" />
                          </SelectTrigger>
                          <SelectContent>
                            {DENIER_OPTIONS.map((d) => (
                              <SelectItem key={d} value={d}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Warp Count</Label>
                        <Input
                          value={newLead.warp_count}
                          onChange={(e) => setNewLead({ ...newLead, warp_count: e.target.value })}
                          placeholder="e.g., 40s"
                          data-testid="lead-warp-count-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Weft Count</Label>
                        <Input
                          value={newLead.weft_count}
                          onChange={(e) => setNewLead({ ...newLead, weft_count: e.target.value })}
                          placeholder="e.g., 40s"
                          data-testid="lead-weft-count-input"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Properties - ONLY for Denim */}
              {newLead.fabric_type === "Denim" && (
                <div className="form-section">
                  <h3 className="form-section-title mb-4">Properties</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Weft Shrinkage (%)</Label>
                      <Input
                        value={newLead.weft_shrinkage}
                        onChange={(e) => setNewLead({ ...newLead, weft_shrinkage: e.target.value })}
                        placeholder="e.g., 3%"
                        data-testid="lead-shrinkage-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stretch (%)</Label>
                      <Input
                        value={newLead.stretch}
                        onChange={(e) => setNewLead({ ...newLead, stretch: e.target.value })}
                        placeholder="e.g., 5%"
                        data-testid="lead-stretch-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">Quantity Required</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity <span className="text-green-600">★</span></Label>
                    <Input
                      id="quantity"
                      value={newLead.quantity}
                      onChange={(e) => setNewLead({ ...newLead, quantity: e.target.value })}
                      placeholder="e.g., 5000"
                      data-testid="lead-quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit <span className="text-green-600">★</span></Label>
                    <Select
                      value={newLead.quantity_unit}
                      onValueChange={(value) => setNewLead({ ...newLead, quantity_unit: value })}
                    >
                      <SelectTrigger data-testid="lead-quantity-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownOptions?.quantity_units || ["Yards", "Meters", "Kgs"]).map((u) => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Sample Shared */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">Sample Information <span className="text-green-600 text-sm">★</span></h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Label className="text-slate-700">Sample shared by customer? <span className="text-green-600">★</span></Label>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant={newLead.sample_shared ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNewLead({ ...newLead, sample_shared: true })}
                        className={newLead.sample_shared ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                        data-testid="sample-yes-btn"
                      >
                        Yes
                      </Button>
                      <Button
                        type="button"
                        variant={!newLead.sample_shared ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setNewLead({ ...newLead, sample_shared: false });
                          setSampleFile(null);
                        }}
                        className={!newLead.sample_shared ? "bg-slate-600 hover:bg-slate-700" : ""}
                        data-testid="sample-no-btn"
                      >
                        No
                      </Button>
                    </div>
                  </div>
                  
                  {newLead.sample_shared && (
                    <div className="space-y-2">
                      <Label className="text-slate-700">Upload Sample Image</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(e) => setSampleFile(e.target.files[0])}
                          className="max-w-xs"
                          data-testid="sample-file-input"
                        />
                        {sampleFile && (
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <CheckSquare className="w-4 h-4" />
                            {sampleFile.name}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">Accepted formats: JPEG, PNG, WEBP</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="form-section">
                <h3 className="form-section-title mb-4">Notes</h3>
                <Textarea
                  id="notes"
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  rows={4}
                  placeholder="Add any additional notes about this lead..."
                  data-testid="lead-notes-input"
                />
              </div>

              <DialogFooter className="sticky bottom-0 bg-white pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={isSubmitting}
                  data-testid="submit-lead-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : "Create Lead"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leads by name, company, or phone..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-leads-input"
              />
            </div>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={hasActiveFilters ? "border-blue-600 text-blue-600" : ""}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {[filters.last_order_date, filters.flagged, filters.order_fabric_type, filters.order_category].filter(Boolean).length
                    + (filters.data_source?.length > 0 ? 1 : 0)
                    + (filters.fabric_category?.length > 0 ? 1 : 0)
                    + (filters.allocation_status?.length > 0 ? 1 : 0)
                    + (filters.status?.length > 0 ? 1 : 0)
                    + (filters.payment_type?.length > 0 ? 1 : 0)
                    + (filters.city?.length > 0 ? 1 : 0)
                    + (filters.order_timeline?.length > 0 ? 1 : 0)
                    + (filters.gst_turnover?.length > 0 ? 1 : 0)
                    + (filters.primary_fabric_material?.length > 0 ? 1 : 0)
                    + (filters.assigned_to?.length > 0 ? 1 : 0)
                    + (filters.filter_fabric_types?.length > 0 ? 1 : 0)
                    + (filters.filter_regions?.length > 0 ? 1 : 0)
                    + ((filters.score_min !== "" || filters.score_max !== "") ? 1 : 0)}
                </span>
              )}
            </Button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 animate-fade-in">
              {/* Basic Filters */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-9 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Data Source</Label>
                  <MultiSelectFilter
                    value={filters.data_source}
                    onChange={(v) => setFilters({ ...filters, data_source: v })}
                    options={[...new Set([...INDIA_DATA_SOURCES, ...BANGLADESH_DATA_SOURCES])]}
                    placeholder="All sources"
                    searchable
                    testid="filter-data-source-select"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GST Turnover</Label>
                  <MultiSelectFilter
                    value={filters.gst_turnover}
                    onChange={(v) => setFilters({ ...filters, gst_turnover: v })}
                    options={["0-40L", "40L-1.5Cr", "1.5Cr-5Cr", "5Cr-25Cr", "25Cr-100Cr", "100Cr-500Cr", "500Cr+"]}
                    placeholder="All brackets"
                    testid="filter-gst-turnover-select"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fabric Category</Label>
                  <MultiSelectFilter
                    value={filters.fabric_category}
                    onChange={(v) => setFilters({ ...filters, fabric_category: v })}
                    options={PRODUCT_CATEGORIES}
                    placeholder="All categories"
                    searchable
                    testid="filter-category-select"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Allocation Status</Label>
                  <MultiSelectFilter
                    value={filters.allocation_status}
                    onChange={(v) => setFilters({ ...filters, allocation_status: v })}
                    options={ALLOCATION_STATUSES.map(s => ({ value: s.value, label: s.label }))}
                    placeholder="All statuses"
                    testid="filter-allocation-status-select"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Activity Status</Label>
                  <MultiSelectFilter
                    value={filters.status}
                    onChange={(v) => setFilters({ ...filters, status: v })}
                    options={STATUS_OPTIONS.map(s => ({ value: s.value, label: s.label }))}
                    placeholder="All statuses"
                    testid="filter-status-select"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Payment Type</Label>
                  <MultiSelectFilter
                    value={filters.payment_type}
                    onChange={(v) => setFilters({ ...filters, payment_type: v })}
                    options={[{ value: "Cash", label: "Cash" }, { value: "Credit", label: "Credit" }, { value: "_not_set_", label: "Not Set" }]}
                    placeholder="All"
                    testid="filter-payment-type-select"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">City</Label>
                  <MultiSelectFilter
                    value={filters.city}
                    onChange={(v) => setFilters({ ...filters, city: v })}
                    options={[{ value: "_not_set_", label: "Not Set" }, ...distinctCities.map(c => ({ value: c, label: c }))]}
                    placeholder="All cities"
                    searchable
                    testid="filter-city-select"
                  />
                </div>
                {/* Fabric Type Multi-select Dropdown */}
                <div className="space-y-1">
                  <Label className="text-xs">Fabric Type</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full h-9 justify-between text-sm font-normal ${filters.filter_fabric_types?.length > 0 ? 'border-blue-400 bg-blue-50 text-blue-700' : 'text-slate-500'}`}
                        data-testid="filter-fabric-type-btn"
                      >
                        {filters.filter_fabric_types?.length > 0
                          ? `${filters.filter_fabric_types.length} selected`
                          : "All types"}
                        <Filter className="w-3 h-3 ml-1 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="start">
                      <div className="space-y-1">
                        {FABRIC_TYPES.map((ft) => (
                          <label key={ft} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer" data-testid={`filter-ft-${ft.toLowerCase()}`}>
                            <input
                              type="checkbox"
                              checked={filters.filter_fabric_types?.includes(ft)}
                              onChange={(e) => {
                                const curr = filters.filter_fabric_types || [];
                                setFilters({ ...filters, filter_fabric_types: e.target.checked ? [...curr, ft] : curr.filter(v => v !== ft) });
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-700">{ft}</span>
                          </label>
                        ))}
                      </div>
                      {filters.filter_fabric_types?.length > 0 && (
                        <button
                          onClick={() => setFilters({ ...filters, filter_fabric_types: [] })}
                          className="w-full mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500 hover:text-red-500 text-center"
                        >
                          Clear
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                {/* Region Multi-select Dropdown */}
                <div className="space-y-1">
                  <Label className="text-xs">Region</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full h-9 justify-between text-sm font-normal ${filters.filter_regions?.length > 0 ? 'border-blue-400 bg-blue-50 text-blue-700' : 'text-slate-500'}`}
                        data-testid="filter-region-btn"
                      >
                        {filters.filter_regions?.length > 0
                          ? filters.filter_regions.join(', ')
                          : "All regions"}
                        <Globe className="w-3 h-3 ml-1 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-44 p-2" align="start">
                      <div className="space-y-1">
                        {["India", "Bangladesh"].map((r) => (
                          <label key={r} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer" data-testid={`filter-region-${r.toLowerCase()}`}>
                            <input
                              type="checkbox"
                              checked={filters.filter_regions?.includes(r)}
                              onChange={(e) => {
                                const curr = filters.filter_regions || [];
                                setFilters({ ...filters, filter_regions: e.target.checked ? [...curr, r] : curr.filter(v => v !== r) });
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-700">{r}</span>
                          </label>
                        ))}
                      </div>
                      {filters.filter_regions?.length > 0 && (
                        <button
                          onClick={() => setFilters({ ...filters, filter_regions: [] })}
                          className="w-full mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500 hover:text-red-500 text-center"
                        >
                          Clear
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                {/* ICP Score Range Filter — user-defined min/max */}
                <div className="space-y-1">
                  <Label className="text-xs">Order Timeline</Label>
                  <MultiSelectFilter
                    value={filters.order_timeline}
                    onChange={(v) => setFilters({ ...filters, order_timeline: v })}
                    options={["Within a week", "Within 15 days", "Within 30 days", "1-3 months", "3-6 months", "6+ months"]}
                    placeholder="Any timeline"
                    testid="filter-order-timeline"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">ICP Score (0-10)</Label>
                  <div className={`flex items-center gap-1 ${(filters.score_min || filters.score_max) ? 'text-blue-700' : ''}`}>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      placeholder="Min"
                      value={filters.score_min ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters({ ...filters, score_min: v === "" ? "" : v });
                      }}
                      className={`h-9 text-sm ${filters.score_min ? 'border-blue-400 bg-blue-50' : ''}`}
                      data-testid="filter-score-min"
                    />
                    <span className="text-slate-400 text-xs">to</span>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      placeholder="Max"
                      value={filters.score_max ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters({ ...filters, score_max: v === "" ? "" : v });
                      }}
                      className={`h-9 text-sm ${filters.score_max ? 'border-blue-400 bg-blue-50' : ''}`}
                      data-testid="filter-score-max"
                    />
                    {(filters.score_min || filters.score_max) && (
                      <button
                        type="button"
                        onClick={() => setFilters({ ...filters, score_min: "", score_max: "" })}
                        className="text-xs text-slate-500 hover:text-red-600 px-1"
                        title="Clear"
                        data-testid="filter-score-clear"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {['admin','super_admin'].includes(currentUser?.role) && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Smart Filters (Admin)</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Assigned To (Multi-select)</Label>
                      <div className="border rounded-md p-2 max-h-[160px] overflow-y-auto space-y-1 bg-white">
                        <label className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer" data-testid="filter-agent-unassigned">
                          <input
                            type="checkbox"
                            checked={filters.assigned_to?.includes('unassigned')}
                            onChange={(e) => {
                              const curr = filters.assigned_to || [];
                              setFilters({ ...filters, assigned_to: e.target.checked ? [...curr, 'unassigned'] : curr.filter(a => a !== 'unassigned') });
                            }}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm text-slate-600 italic">Unassigned</span>
                        </label>
                        {agents.map((agent) => (
                          <label key={agent.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer" data-testid={`filter-agent-${agent.id}`}>
                            <input
                              type="checkbox"
                              checked={filters.assigned_to?.includes(agent.id)}
                              onChange={(e) => {
                                const curr = filters.assigned_to || [];
                                setFilters({ ...filters, assigned_to: e.target.checked ? [...curr, agent.id] : curr.filter(a => a !== agent.id) });
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-700">{agent.name}</span>
                          </label>
                        ))}
                      </div>
                      {filters.assigned_to?.length > 0 && (
                        <p className="text-xs text-blue-600">{filters.assigned_to.length} selected</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Material</Label>
                      <MultiSelectFilter
                        value={filters.primary_fabric_material}
                        onChange={(v) => setFilters({ ...filters, primary_fabric_material: v })}
                        options={PRIMARY_MATERIALS}
                        placeholder="All materials"
                        searchable
                        testid="filter-primary-material"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unallocated Duration</Label>
                      <Select
                        value={filters.unallocated_days || "_all_"}
                        onValueChange={(value) => setFilters({ ...filters, unallocated_days: value === "_all_" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All leads" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all_">All leads</SelectItem>
                          <SelectItem value="15">15+ days unallocated</SelectItem>
                          <SelectItem value="30">30+ days unallocated</SelectItem>
                          <SelectItem value="45">45+ days unallocated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Last Allocation Date</Label>
                      <Input
                        type="date"
                        value={filters.last_allocation_date}
                        onChange={(e) => setFilters({ ...filters, last_allocation_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Call Date</Label>
                      <Input
                        type="date"
                        value={filters.last_call_date}
                        onChange={(e) => setFilters({ ...filters, last_call_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-red-600 font-semibold">⚠️ Flagged Leads</Label>
                      <Select
                        value={filters.flagged || "_all_"}
                        onValueChange={(value) => setFilters({ ...filters, flagged: value === "_all_" ? "" : value })}
                      >
                        <SelectTrigger className={filters.flagged === "true" ? "border-red-300 bg-red-50" : ""}>
                          <SelectValue placeholder="All leads" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all_">All Leads</SelectItem>
                          <SelectItem value="true">🚩 Show Flagged Only</SelectItem>
                          <SelectItem value="false">✓ Hide Flagged</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-600 font-semibold">📦 Recent Order Fabric</Label>
                      <Select
                        value={filters.order_fabric_type || "_all_"}
                        onValueChange={(value) => setFilters({ ...filters, order_fabric_type: value === "_all_" ? "" : value })}
                      >
                        <SelectTrigger className={filters.order_fabric_type ? "border-purple-300 bg-purple-50" : ""}>
                          <SelectValue placeholder="All fabric types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all_">All Fabric Types</SelectItem>
                          <SelectItem value="Greige">Greige</SelectItem>
                          <SelectItem value="Dyed">Dyed</SelectItem>
                          <SelectItem value="Printed">Printed</SelectItem>
                          <SelectItem value="Denim">Denim</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-teal-600 font-semibold">🧵 Recent Order Category</Label>
                      <Select
                        value={filters.order_category || "_all_"}
                        onValueChange={(value) => setFilters({ ...filters, order_category: value === "_all_" ? "" : value })}
                        data-testid="order-category-filter"
                      >
                        <SelectTrigger className={filters.order_category ? "border-teal-300 bg-teal-50" : ""} data-testid="order-category-trigger">
                          <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all_">All Categories</SelectItem>
                          <SelectItem value="Cotton">Cotton</SelectItem>
                          <SelectItem value="Polyester">Polyester</SelectItem>
                          <SelectItem value="Rayon">Rayon</SelectItem>
                          <SelectItem value="Viscose">Viscose</SelectItem>
                          <SelectItem value="Linen">Linen</SelectItem>
                          <SelectItem value="Lycra">Lycra</SelectItem>
                          <SelectItem value="Tencel">Tencel</SelectItem>
                          <SelectItem value="Modal">Modal</SelectItem>
                          <SelectItem value="Spandex">Spandex</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-orange-600 font-semibold">Last Order Date</Label>
                      <Select
                        value={filters.last_order_date || "_all_"}
                        onValueChange={(value) => setFilters({ ...filters, last_order_date: value === "_all_" ? "" : value })}
                      >
                        <SelectTrigger className={filters.last_order_date ? "border-orange-300 bg-orange-50" : ""} data-testid="last-order-date-trigger">
                          <SelectValue placeholder="All leads" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all_">All Leads</SelectItem>
                          <SelectItem value="lt15">Less than 15 days</SelectItem>
                          <SelectItem value="15-30">15-30 days</SelectItem>
                          <SelectItem value="30-45">30-45 days</SelectItem>
                          <SelectItem value="gt45">More than 45 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
              {hasActiveFilters && (
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-slate-500"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear filters
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSaveFilter(true)}
                    className="text-blue-600 border-blue-200"
                    data-testid="save-filter-btn"
                  >
                    <Save className="w-3.5 h-3.5 mr-1" />
                    Save Filter
                  </Button>
                </div>
              )}
              {/* Save Filter Input */}
              {showSaveFilter && (
                <div className="mt-3 flex items-center gap-2">
                  <Input
                    value={saveFilterName}
                    onChange={(e) => setSaveFilterName(e.target.value)}
                    placeholder="Filter preset name..."
                    className="h-8 max-w-[200px] text-sm"
                    data-testid="save-filter-name-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveFilter()}
                    autoFocus
                  />
                  <Button size="sm" onClick={handleSaveFilter} disabled={!saveFilterName.trim()} className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs">
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowSaveFilter(false); setSaveFilterName(""); }} className="h-8 text-xs">
                    Cancel
                  </Button>
                </div>
              )}
              {/* Saved Filter Presets */}
              {savedFilters.length > 0 && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500 font-medium">Saved:</span>
                  {savedFilters.map((preset, idx) => (
                    <div key={idx} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full pl-3 pr-1 py-0.5">
                      <button
                        onClick={() => handleLoadFilter(preset)}
                        className="text-xs font-medium text-blue-700 hover:text-blue-900"
                        data-testid={`load-filter-${idx}`}
                      >
                        {preset.name}
                      </button>
                      <button
                        onClick={() => handleDeleteFilter(idx)}
                        className="w-4 h-4 flex items-center justify-center text-blue-400 hover:text-red-500 rounded-full"
                        data-testid={`delete-filter-${idx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-zebra table-dense">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-10 py-3 px-3">
                  <Checkbox 
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Lead</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden lg:table-cell">Assigned To</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden xl:table-cell">Data Source</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden sm:table-cell">Composition</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden md:table-cell">Recent Order</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden sm:table-cell">Last Call</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden lg:table-cell">Last Attempted</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden md:table-cell">Next Follow-up</th>
                <th className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4 hidden sm:table-cell">Score</th>
                <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Status</th>
                <th className="text-right text-xs font-semibold text-slate-600 uppercase tracking-wide py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={12} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-12 text-slate-400">
                    No leads found. {hasActiveFilters ? "Try adjusting your filters." : "Add your first lead!"}
                  </td>
                </tr>
              ) : (
                leads.map((lead) => {
                  const isSelected = selectedLeads.includes(lead.id);
                  // Compute most recent order once for Composition + Recent Order columns
                  const getMostRecentOrder = (orders) => {
                    if (!orders || orders.length === 0) return null;
                    if (orders.length === 1) return orders[0];
                    const ordersWithDates = orders.map(o => {
                      let date = null;
                      if (o.order_date) {
                        const parsed = new Date(o.order_date);
                        if (!isNaN(parsed.getTime())) date = parsed;
                      }
                      return { ...o, _parsedDate: date };
                    });
                    const validDates = ordersWithDates.filter(o => o._parsedDate);
                    if (validDates.length > 0) {
                      validDates.sort((a, b) => b._parsedDate - a._parsedDate);
                      return validDates[0];
                    }
                    return orders[orders.length - 1];
                  };
                  const recentOrder = getMostRecentOrder(lead.order_history);
                  return (
                    <tr 
                      key={lead.id} 
                      className={`hover:bg-slate-50/80 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`} 
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="py-3 px-3">
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectLead(lead.id)}
                          data-testid={`select-lead-${lead.id}`}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${lead.data_quality_feedback ? 'bg-red-100' : 'bg-blue-100'} rounded-full flex items-center justify-center flex-shrink-0 relative`}>
                            <span className={`${lead.data_quality_feedback ? 'text-red-600' : 'text-blue-600'} font-medium text-sm`}>
                              {(lead.company_name || lead.name)?.charAt(0).toUpperCase()}
                            </span>
                            {lead.data_quality_feedback && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-[10px]">!</span>
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <Link 
                                to={`/leads/${lead.id}`}
                                className="font-medium text-slate-900 hover:text-blue-600 truncate block"
                              >
                                {lead.company_name || lead.name}
                              </Link>
                              {lead.data_quality_feedback && (
                                <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap">
                                  ⚠️ Flagged
                                </span>
                              )}
                              {lead.order_timeline === "Within a week" && (
                                <span
                                  className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap flex items-center gap-0.5"
                                  data-testid={`within-week-pill-${lead.id}`}
                                  title="Order timeline: Within a week"
                                >
                                  🔥 Within a week
                                </span>
                              )}
                            </div>
                            {(lead.name || lead.contact_person) && (
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <User className="w-3 h-3" />
                                <span className="truncate">{lead.name || lead.contact_person}</span>
                              </div>
                            )}
                            <p className="font-mono text-xs text-slate-400 mt-0.5">{lead.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 hidden lg:table-cell">
                        {lead.assigned_to_name ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                              <span className="text-emerald-700 font-medium text-xs">
                                {lead.assigned_to_name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-slate-700 block">{lead.assigned_to_name}</span>
                              {lead.allocated_at && (
                                <span className="text-[10px] text-slate-400">
                                  {new Date(lead.allocated_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="py-3 px-4 hidden xl:table-cell">
                        {lead.data_source ? (
                          <div className="space-y-1">
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                              {lead.data_source}
                            </span>
                            {lead.data_source === 'Order exists' && lead.category_serviced_1 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-[10px]">{lead.category_serviced_1}</span>
                                {lead.category_serviced_2 && <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-[10px]">{lead.category_serviced_2}</span>}
                                {lead.category_serviced_3 && <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-[10px]">{lead.category_serviced_3}</span>}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell">
                        <span className="text-xs font-medium text-slate-700" data-testid={`lead-composition-${lead.id}`}>
                          {(lead.data_source === 'Order exists' || lead.data_source === 'Query exists')
                            ? (recentOrder?.composition || '-')
                            : (lead.material_1 
                                ? `${lead.material_1}${lead.material_1_percentage ? ` ${lead.material_1_percentage}%` : ''}` 
                                : '-')}
                        </span>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">
                        {recentOrder ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-medium text-slate-700">
                                {recentOrder.fabric_type || '-'}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                {recentOrder.order_date || ''}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell">
                        {lead.last_call_status ? (
                          <span className={`px-2 py-1 rounded text-xs font-medium border inline-block w-fit ${getCallStatusStyle(lead.last_call_status)}`} data-testid={`last-call-status-${lead.id}`}>
                            {formatCallStatus(lead.last_call_status)}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No calls</span>
                        )}
                      </td>
                      {/* Last Attempted — when the most recent call was made */}
                      <td className="py-3 px-4 hidden lg:table-cell" data-testid={`last-attempted-${lead.id}`}>
                        {lead.last_call_at ? (() => {
                          const d = new Date(lead.last_call_at);
                          const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
                          let relative;
                          if (diffMin < 1) relative = 'just now';
                          else if (diffMin < 60) relative = `${diffMin}m ago`;
                          else if (diffMin < 1440) relative = `${Math.floor(diffMin / 60)}h ago`;
                          else if (diffMin < 10080) relative = `${Math.floor(diffMin / 1440)}d ago`;
                          else relative = d.toLocaleDateString([], { day: '2-digit', month: 'short' });
                          return (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-slate-700 tabular-nums">{d.toLocaleDateString([], { day: '2-digit', month: 'short' })}</span>
                              <span className="text-[10px] text-slate-500">{relative}</span>
                            </div>
                          );
                        })() : (
                          <span className="text-xs text-slate-400 italic">—</span>
                        )}
                      </td>
                      {/* Next Follow-up */}
                      <td className="py-3 px-4 hidden md:table-cell" data-testid={`next-followup-${lead.id}`}>
                        {lead.next_followup_at ? (() => {
                          const d = new Date(lead.next_followup_at);
                          const today = new Date(); today.setHours(0,0,0,0);
                          const target = new Date(d); target.setHours(0,0,0,0);
                          const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
                          let pillCls = 'bg-blue-50 text-blue-700 border-blue-200';
                          let label = d.toLocaleDateString([], { day: '2-digit', month: 'short' });
                          let sub = '';
                          if (diffDays < 0) { pillCls = 'bg-red-50 text-red-700 border-red-200'; sub = `Overdue • ${Math.abs(diffDays)}d`; }
                          else if (diffDays === 0) { pillCls = 'bg-amber-50 text-amber-700 border-amber-200'; sub = 'Today'; }
                          else if (diffDays === 1) { pillCls = 'bg-emerald-50 text-emerald-700 border-emerald-200'; sub = 'Tomorrow'; }
                          else if (diffDays <= 7) { pillCls = 'bg-emerald-50 text-emerald-700 border-emerald-200'; sub = `in ${diffDays}d`; }
                          else { sub = `in ${diffDays}d`; }
                          return (
                            <div className="flex flex-col gap-0.5">
                              <span className={`px-2 py-1 rounded text-xs font-medium border inline-block w-fit ${pillCls}`}>{label}</span>
                              <span className="text-[10px] text-slate-500">{sub}</span>
                            </div>
                          );
                        })() : (
                          <span className="text-xs text-slate-400 italic">—</span>
                        )}
                      </td>
                      {/* ICP Score out of 10 */}
                      <td className="py-3 px-4 text-center hidden sm:table-cell">
                        {lead.icp_score != null ? (
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${
                            lead.icp_score >= 7 ? 'bg-emerald-100 text-emerald-700' :
                            lead.icp_score >= 4 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`} data-testid={`icp-score-${lead.id}`}>
                            {lead.icp_score}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400" data-testid={`icp-score-${lead.id}`}>NA</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-0.5">
                          <span className={`${getStatusColor(getComputedStatus(lead))} px-2 py-1 rounded text-xs font-medium border inline-block w-fit`}>
                            {formatStatus(getComputedStatus(lead))}
                          </span>
                          {lead.pick_pool_ready && lead.pick_pool_pushed_at && (
                            <span className="text-[10px] text-slate-400">
                              {new Date(lead.pick_pool_pushed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* Call button for all users */}
                          {lead.region === "Bangladesh" ? (
                            <Button
                              size="sm"
                              onClick={() => {
                                setManualCallLead(lead);
                                setShowManualCallLog(true);
                              }}
                              className="bg-teal-600 hover:bg-teal-700 text-white h-8 px-3"
                              data-testid={`log-call-${lead.id}`}
                            >
                              <Phone className="w-3 h-3 mr-1" />
                              Log
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => {
                                setCallChoiceLead(lead);
                                setShowCallChoiceDialog(true);
                              }}
                              className="bg-orange-500 hover:bg-orange-600 text-white h-8 px-3"
                              data-testid={`call-lead-${lead.id}`}
                            >
                              <Phone className="w-3 h-3 mr-1" />
                              Call
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`lead-menu-${lead.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => {
                                setManualCallLead(lead);
                                setShowManualCallLog(true);
                              }}>
                                <Phone className="w-4 h-4 mr-2" />
                                Log Call
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteLead(lead.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
            <div className="text-sm text-slate-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total_count)} of {pagination.total_count} leads
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(1)}
                disabled={!pagination.has_prev}
                className="h-8 w-8 p-0"
                data-testid="pagination-first"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevPage}
                disabled={!pagination.has_prev}
                className="h-8 w-8 p-0"
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1 px-2">
                <span className="text-sm font-medium">Page</span>
                <Input
                  type="number"
                  min={1}
                  max={pagination.total_pages}
                  value={pagination.page}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= pagination.total_pages) {
                      goToPage(page);
                    }
                  }}
                  className="w-16 h-8 text-center text-sm"
                  data-testid="pagination-input"
                />
                <span className="text-sm text-slate-500">of {pagination.total_pages}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={!pagination.has_next}
                className="h-8 w-8 p-0"
                data-testid="pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(pagination.total_pages)}
                disabled={!pagination.has_next}
                className="h-8 w-8 p-0"
                data-testid="pagination-last"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* CSV Import Dialog */}
      <CSVImport 
        isOpen={showCSVImport} 
        onClose={() => setShowCSVImport(false)}
        onImportComplete={() => fetchLeads(1)}
      />

      {/* Duplicates Preview & Merge Dialog (Super Admin) */}
      <Dialog open={showDedupDialog} onOpenChange={(open) => { if (!dedupMerging) setShowDedupDialog(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dedup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-rose-600" />
              Remove Duplicate Leads
            </DialogTitle>
            <DialogDescription>
              Preview duplicate leads grouped by GST (India) or Phone (Bangladesh). Click "Merge &amp; Delete" to keep the best lead per group and migrate all call logs / follow-ups / emails onto it.
            </DialogDescription>
          </DialogHeader>

          {dedupLoading && (
            <div className="py-10 flex flex-col items-center gap-2 text-slate-500" data-testid="dedup-loading">
              <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
              <p>Scanning...</p>
            </div>
          )}

          {!dedupLoading && dedupData && (
            <>
              {dedupData.total_groups === 0 ? (
                <div className="py-10 text-center space-y-2" data-testid="dedup-no-duplicates">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                  <h3 className="text-lg font-semibold text-slate-900">No duplicates found</h3>
                  <p className="text-slate-500 text-sm">Your CRM is clean.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-rose-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-rose-700" data-testid="dedup-groups-count">{dedupData.total_groups}</p>
                      <p className="text-xs text-rose-500">Duplicate groups</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-amber-700" data-testid="dedup-to-remove">{dedupData.total_duplicates_to_remove}</p>
                      <p className="text-xs text-amber-500">Leads will be removed</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 space-y-1">
                    <p className="font-semibold">Which lead is kept (priority order):</p>
                    <p>1. Has order_count &gt; 0 OR has_query_in_system → 2. LFB created → 3. Has calls → 4. Is assigned → 5. Most fields filled → 6. Earliest created_at</p>
                    <p className="pt-1">Child records (calls, follow-ups, emails) are <strong>migrated</strong> onto the kept lead before duplicates are deleted.</p>
                  </div>

                  <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                    {dedupData.groups.map((g, gi) => (
                      <div key={gi} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`dedup-group-${gi}`}>
                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded ${g.match_type === 'gst' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {g.match_type === 'gst' ? 'GST' : 'Phone'}
                          </span>
                          <span className="font-mono">{g.key}</span>
                          <span className="text-slate-400 ml-auto">{g.count} leads</span>
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-white border-b border-slate-100">
                            <tr className="text-slate-500">
                              <th className="text-left p-2 font-semibold">Company</th>
                              <th className="text-left p-2 font-semibold">Source</th>
                              <th className="text-left p-2 font-semibold">Assigned</th>
                              <th className="text-center p-2 font-semibold">Orders</th>
                              <th className="text-center p-2 font-semibold">Q/LFB/Calls</th>
                              <th className="text-center p-2 font-semibold">Children</th>
                              <th className="text-left p-2 font-semibold">Created</th>
                              <th className="text-center p-2 font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {g.leads.map((l) => (
                              <tr key={l.id} className={l.will_keep ? "bg-emerald-50" : "bg-rose-50/40"}>
                                <td className="p-2">
                                  <div className="font-medium">{l.company_name || '-'}</div>
                                  <div className="text-[10px] text-slate-400">{l.id.slice(0, 8)}…</div>
                                </td>
                                <td className="p-2 text-slate-600">{l.data_source || '-'}</td>
                                <td className="p-2 text-slate-600">{l.assigned_to_name}</td>
                                <td className="p-2 text-center">{l.order_count}</td>
                                <td className="p-2 text-center text-[10px]">
                                  <span className={l.has_query_in_system ? "text-purple-600" : "text-slate-300"}>Q</span>{" / "}
                                  <span className={l.lfb_created ? "text-emerald-600" : "text-slate-300"}>L</span>{" / "}
                                  <span className={l.call_count > 0 ? "text-blue-600" : "text-slate-300"}>{l.call_count}</span>
                                </td>
                                <td className="p-2 text-center text-[10px] text-slate-500">
                                  {l.calls}c / {l.followups}f / {l.emails}e
                                </td>
                                <td className="p-2 text-slate-500 text-[10px]">
                                  {l.created_at ? new Date(l.created_at).toLocaleDateString() : '-'}
                                </td>
                                <td className="p-2 text-center">
                                  {l.will_keep ? (
                                    <span className="text-emerald-700 font-bold text-[10px] bg-emerald-100 px-2 py-0.5 rounded" data-testid={`keep-${l.id}`}>KEEP</span>
                                  ) : (
                                    <span className="text-rose-700 font-bold text-[10px] bg-rose-100 px-2 py-0.5 rounded" data-testid={`remove-${l.id}`}>REMOVE</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>

                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setShowDedupDialog(false)} disabled={dedupMerging} data-testid="dedup-cancel">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmMerge}
                      disabled={dedupMerging}
                      className="bg-rose-600 hover:bg-rose-700"
                      data-testid="dedup-confirm-merge"
                    >
                      {dedupMerging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                      Merge &amp; Delete {dedupData.total_duplicates_to_remove} Duplicates
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* Mass Allocate Dialog */}
      <Dialog open={showMassAllocate} onOpenChange={setShowMassAllocate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Allocate Leads to Agent
            </DialogTitle>
            <DialogDescription>
              Allocate {selectedLeads.length} selected lead{selectedLeads.length > 1 ? 's' : ''} to an agent
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>{selectedLeads.length}</strong> lead{selectedLeads.length > 1 ? 's' : ''} will be allocated with status <strong>"new"</strong> and today's allocation date.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Select Agent <span className="text-red-500">*</span></Label>
              <Select value={allocateToAgent} onValueChange={setAllocateToAgent}>
                <SelectTrigger data-testid="allocate-agent-select">
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent>
                  {agents.filter(a => a.is_active).map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-700 font-medium text-xs">
                            {agent.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span>{agent.name}</span>
                        <span className="text-slate-400 text-xs">({agent.email})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowMassAllocate(false);
              setAllocateToAgent("");
            }}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleMassAllocate}
              disabled={isAllocating || !allocateToAgent}
              data-testid="confirm-allocate-btn"
            >
              {isAllocating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Allocate Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super-admin Pick Pool lock override dialog */}
      <Dialog
        open={pickPoolOverride.open}
        onOpenChange={(o) => !o && setPickPoolOverride({ open: false, message: "", blocked: [], reason: "" })}
      >
        <DialogContent className="sm:max-w-lg" data-testid="pickpool-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Override Pick Pool Lock
            </DialogTitle>
            <DialogDescription>
              This action will be audit-logged against your name. Only proceed if you have a business reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-900 leading-relaxed">
              {pickPoolOverride.message}
            </div>
            {pickPoolOverride.blocked?.length > 0 && (
              <div className="text-xs text-slate-600 max-h-32 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
                {pickPoolOverride.blocked.map((b) => (
                  <div key={b.lead_id} className="flex justify-between">
                    <span className="truncate">{b.company_name}</span>
                    <span className="text-slate-400 ml-2 shrink-0">{b.days_remaining}d left • pushed by {b.pushed_by}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="override-reason" className="text-sm">
                Reason for override <span className="text-red-500">*</span>
                <span className="text-slate-400 text-xs ml-2">(min 10 characters)</span>
              </Label>
              <textarea
                id="override-reason"
                data-testid="pickpool-override-reason"
                rows={3}
                className="w-full border border-slate-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                value={pickPoolOverride.reason}
                onChange={(e) => setPickPoolOverride((s) => ({ ...s, reason: e.target.value }))}
                placeholder="e.g. Agent has resigned and customer needs an active POC for ongoing LFB."
              />
              <p className="text-[11px] text-slate-400">{pickPoolOverride.reason.trim().length} / 10 characters</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPickPoolOverride({ open: false, message: "", blocked: [], reason: "" })}
              disabled={isOverrideSubmitting}
              data-testid="pickpool-override-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={submitPickPoolOverride}
              disabled={isOverrideSubmitting || pickPoolOverride.reason.trim().length < 10}
              data-testid="pickpool-override-confirm"
            >
              {isOverrideSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
              Override & Allocate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mass Delete Confirmation Dialog */}
      <Dialog open={showMassDelete} onOpenChange={setShowMassDelete}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Leads
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''}?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ This action cannot be undone!
              </p>
              <p className="text-sm text-red-700 mt-2">
                <strong>{selectedLeads.length}</strong> lead{selectedLeads.length > 1 ? 's' : ''} will be permanently deleted along with all associated call history and notes.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowMassDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleMassDelete}
              disabled={isDeleting}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete {selectedLeads.length} Lead{selectedLeads.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Choice Dialog — Indian leads */}
      <Dialog open={showCallChoiceDialog} onOpenChange={setShowCallChoiceDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>How do you want to call?</DialogTitle>
            <DialogDescription>{callChoiceLead?.company_name} — {callChoiceLead?.phone}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white justify-start gap-3"
              onClick={() => {
                setShowCallChoiceDialog(false);
                if (callChoiceLead) handleClickToCall(callChoiceLead);
              }}
              data-testid="choice-call-panel"
            >
              <PhoneOutgoing className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">Call via Panel</div>
                <div className="text-xs opacity-80">Exotel click-to-call</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full h-12 justify-start gap-3 border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={() => {
                setShowCallChoiceDialog(false);
                setManualCallLead(callChoiceLead);
                setShowManualCallLog(true);
              }}
              data-testid="choice-log-manual"
            >
              <History className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">Log Call Manually</div>
                <div className="text-xs opacity-80">Record date, duration & screenshot</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Call Log Dialog */}
      <Dialog open={showManualCallLog} onOpenChange={setShowManualCallLog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log Call - {manualCallLead?.company_name}</DialogTitle>
            <DialogDescription>
              Manually log a call for this lead
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Call Date *</Label>
                <Input
                  type="date"
                  value={manualCallData.call_date}
                  onChange={(e) => setManualCallData(prev => ({
                    ...prev,
                    call_date: e.target.value,
                    followup_date: prev.followup_date_touched ? prev.followup_date : addDaysToDate(e.target.value, 7),
                  }))}
                  data-testid="manual-call-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Call Time *</Label>
                <Input
                  type="time"
                  value={manualCallData.call_time}
                  onChange={(e) => setManualCallData(prev => ({ ...prev, call_time: e.target.value }))}
                  data-testid="manual-call-time"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select
                value={manualCallData.status}
                onValueChange={(value) => setManualCallData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="manual-call-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">Call Connected</SelectItem>
                  <SelectItem value="requirement_shared">Requirement Shared</SelectItem>
                  <SelectItem value="call_again_later">Call Again Later</SelectItem>
                  <SelectItem value="no_requirement">No Requirement Currently</SelectItem>
                  <SelectItem value="not_connected">Call Not Connected</SelectItem>
                  <SelectItem value="not_relevant">Not Relevant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={manualCallData.duration_minutes}
                  onChange={(e) => setManualCallData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                  placeholder="0"
                  data-testid="manual-call-duration-min"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (seconds)</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={manualCallData.duration_seconds_input}
                  onChange={(e) => setManualCallData(prev => ({ ...prev, duration_seconds_input: e.target.value }))}
                  placeholder="0"
                  data-testid="manual-call-duration-sec"
                />
              </div>
            </div>
            {/* Call Screenshot — Bangladesh Integrity Rule */}
            {(() => {
              const totalSec = (parseInt(manualCallData.duration_minutes) || 0) * 60 + (parseInt(manualCallData.duration_seconds_input) || 0);
              const isBD = (manualCallLead?.region || '') === 'Bangladesh';
              const needsProof = isBD && totalSec >= 300;
              return (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Call Screenshot
                    {needsProof && (
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid="leads-bd-screenshot-flag">
                        Required for 5+ min
                      </span>
                    )}
                  </Label>
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => setCallScreenshotFile(e.target.files[0] || null)}
                    data-testid="manual-call-screenshot"
                    className={`text-sm ${needsProof && !callScreenshotFile ? 'border-red-300' : ''}`}
                  />
                  {callScreenshotFile && (
                    <p className="text-xs text-emerald-600">{callScreenshotFile.name}</p>
                  )}
                  {needsProof && !callScreenshotFile && (
                    <p className="text-[11px] text-red-600">
                      🚩 Bangladesh Integrity Rule: 5+ min calls need a WhatsApp screenshot OR a note of 20+ characters as proof.
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={manualCallData.notes}
                onChange={(e) => setManualCallData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Call notes..."
                data-testid="manual-call-notes"
              />
            </div>

            {/* Mandatory Follow-up — required for agents on every call */}
            <div className="space-y-3 pt-3 mt-2 border-t border-slate-200" data-testid="leads-manual-call-followup-section">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  Schedule Follow-up
                  <span className="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Optional</span>
                </Label>
                <span className="text-[11px] text-slate-400">auto: call date + 7 days</span>
              </div>
              <Textarea
                rows={2}
                value={manualCallData.followup_description}
                onChange={(e) => setManualCallData(prev => ({ ...prev, followup_description: e.target.value }))}
                placeholder="What is the next action? e.g. Call back to share fabric swatch, send pricing, follow on payment…"
                data-testid="leads-manual-call-followup-desc"
              />
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1 col-span-1">
                  <Label className="text-xs text-slate-500">Follow-up Date</Label>
                  <Input
                    type="date"
                    value={manualCallData.followup_date}
                    onChange={(e) => setManualCallData(prev => ({ ...prev, followup_date: e.target.value, followup_date_touched: true }))}
                    data-testid="leads-manual-call-followup-date"
                  />
                </div>
                <div className="space-y-1 col-span-1">
                  <Label className="text-xs text-slate-500">Time</Label>
                  <Input
                    type="time"
                    value={manualCallData.followup_time}
                    onChange={(e) => setManualCallData(prev => ({ ...prev, followup_time: e.target.value }))}
                    data-testid="leads-manual-call-followup-time"
                  />
                </div>
                <div className="space-y-1 col-span-1">
                  <Label className="text-xs text-slate-500">Priority</Label>
                  <Select
                    value={manualCallData.followup_priority}
                    onValueChange={(v) => setManualCallData(prev => ({ ...prev, followup_priority: v }))}
                  >
                    <SelectTrigger data-testid="leads-manual-call-followup-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualCallLog(false)}>Cancel</Button>
            <Button onClick={handleManualCallLog} disabled={isUploadingCallScreenshot} data-testid="manual-call-submit">
              {isUploadingCallScreenshot ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
              Log Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
